import { isAnalyticsAllowed } from '../analytics/consentGate'
import type { UploadSource } from '../analytics/generated/analyticsEvents'
import {
  normalizeOnboardingScope,
  onboardingScopeQuery,
  validateScopedOnboardingRun,
  type OnboardingAdvanceOptions,
  type OnboardingCapabilities,
  type OnboardingCsvRequest,
  type OnboardingCsvResult,
  type OnboardingEmailPreview,
  type OnboardingEmailTemplate,
  type OnboardingEmailTemplateDraft,
  type OnboardingEmailTemplatePreview,
  type OnboardingOrgRoster,
  type OnboardingOrgSummary,
  type OnboardingPreflight,
  type OnboardingRun,
  type OnboardingRunTargetInput,
  type OnboardingSettleOutcome,
  type OnboardingStepName,
  type OnboardingTarget,
  type OnboardingTeamLookup,
  type OnboardingTestSendResult,
} from './onboardingTypes'
import type { VideoByomProtocolConfigV1 } from '../generated/videoByomProtocol'
import type {
  Project,
  ProjectListItem,
  PaginatedResponse,
  CreateProjectResponse,
  CreateProjectRequest,
  ProjectStatus,
  ProjectConfig,
  UpdateProjectConfigRequest,
  DashboardTab,
  TabScope,
  TabSortBy,
  TabSortDirection,
  TemplateCatalogResponse,
  UpscaleTargetResolution,
} from './types'
import {
  type ImageAnnotationDict,
  type LocationPanoramaViewDict,
  type SoundGeneratedAudioDict,
  type TimelineConfigDict,
} from '../generated/nodeDataTypes'
import {
  decodeCanvasChangeHints,
  decodeInternalModelEntitlementItem,
  decodeInternalModelEntitlementListResponse,
  decodeVideoEditorAudioActionRunResponse,
  decodeVideoEditorAudioActionsResponse,
  type ApiValue,
  type CanvasChangeHints,
  type InternalModelEntitlementItem,
  type InternalModelEntitlementListResponse,
  type VideoEditorAudioActionRunResponse,
  type VideoEditorAudioActionId,
  type VideoEditorAudioActionsResponse,
} from './responseDecoders'
import { getDefaultApiBase } from '../utils/environment'
import { DEFAULT_CANVAS_ID } from '../contexts/ActiveCanvasIdContext'
import { createDebug, createNadDebug } from '../utils/debugLog'
import { trackNodeGenerationSubmitted, type ExecuteNodeAnalytics } from '../analytics/nodeAnalytics'
import i18n, { LOCALE_STORAGE_KEY } from '../i18n/i18n'
import { normalizeLocale, type SupportedLocale } from '../i18n/supportedLocales'
import {
  decodeBillingCatalog,
  decodeCheckoutCountries,
  type CheckoutCountries,
  decodeCheckoutOrder,
  decodeHandoff,
  type CheckoutHandoff,
  type CheckoutDestination,
  type CheckoutOrder,
  type BillingCatalog,
  type CheckoutCountryRequest,
} from '../features/billing/billingTypes'
import { decodeC2paDownload, type C2paDownloadResult } from './c2paDownload'
import {
  MAX_FILE_SIZE,
  getFileSizeError,
  uploadMediaTypeFromMime,
} from '../utils/uploadFileValidation'

export {
  decodeDirectorToolListResponse,
  decodeInternalModelEntitlementItem,
  decodeInternalModelEntitlementListResponse,
  decodeVideoEditorAudioActionRunResponse,
  decodeVideoEditorAudioActionsResponse,
} from './responseDecoders'
export type {
  ApiValue,
  CanvasChangeHints,
  CanvasObservedNodeHint,
  DirectorToolDefinition,
  DirectorToolListResponse,
  InternalModelEntitlementItem,
  InternalModelEntitlementListResponse,
  VideoEditorAudioAction,
  VideoEditorAudioActionDisabledReason,
  VideoEditorAudioActionId,
  VideoEditorAudioActionRunResponse,
  VideoEditorAudioActionRunStatus,
  VideoEditorAudioActionsResponse,
  VideoEditorAudioActionState,
  VideoEditorAudioSourceKind,
  VideoEditorSpeechSegment,
  VideoEditorAudioTarget,
} from './responseDecoders'

// Per-canvas config endpoints (`/agentic/config`) default to the "agent" canvas
// server-side, so we append `canvas_id` only for a non-default (secondary) canvas
// — keeping the default-canvas request byte-identical to before this change.
function canvasConfigQuery(canvasId?: string): string {
  if (!canvasId || canvasId === DEFAULT_CANVAS_ID) return ''
  return `?canvas_id=${encodeURIComponent(canvasId)}`
}

// Transient-failure retry interceptor — see withTransientRetry below.
// Debug logger gated by VITE_NAD_DEBUG / `localStorage.debug = 'dw:retry:*'`.
const debugTransientRetry = createDebug('dw:retry:transient')

// NAD trace for canvas-scoped node execution/cancel. Emits [nad-json] lines
// (feature tag `canvas-node-execute`) so `nad trace` can confirm the canvas_id
// carried on each execute-node / cancel-node request. Dev-gated by VITE_NAD_DEBUG.
const canvasExecuteLog = createNadDebug('dw:canvas-node-execute:client')
const assetUploadLog = createNadDebug('dw:asset-upload:client')

type ApiRecord = Record<string, ApiValue>

function parseJsonResponse<T>(text: string): T {
  return JSON.parse(text)
}

function isApiRecord(value: ApiValue | null): value is ApiRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringField(record: ApiRecord, key: string): string | undefined {
  const value = record[key]
  return typeof value === 'string' ? value : undefined
}

// Storage keys
const API_BASE_KEY = 'dreamwright_api_base'
const DIRECTOR_MODEL_KEY = 'dreamwright_director_model'
const VIDEO_MODEL_KEY = 'dreamwright_video_model'
const VIDEO_SIMULATOR_MODE_KEY = 'dreamwright_video_simulator_mode'

// Auth token getter - set by AuthContext to provide Supabase JWT
let authTokenGetter: (() => Promise<string | null>) | null = null
// Cached token for sync access (used for asset URLs in <img> tags)
let cachedAuthToken: string | null = null

// Publication token for unauthenticated access to published project assets.
// Set when viewing a published project page; used as fallback when asset URLs
// in the snapshot weren't rewritten to signed Supabase URLs.
let currentPublicationToken: string | null = null

export function setPublicationToken(token: string | null): void {
  currentPublicationToken = token
}

export function getPublicationToken(): string | null {
  return currentPublicationToken
}

export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  authTokenGetter = getter
  // Update cached token immediately
  getter().then(token => {
    cachedAuthToken = token
  })
}

export function setCachedAuthToken(token: string | null): void {
  cachedAuthToken = token
}

// account-self-service: Optional global hook invoked on any 4xx response so
// AuthContext can detect ``{"detail": {"restore_required": true}}`` payloads
// and route the user to /account/restore.  Set to null to disable.
type UnauthorizedHandler = (res: Response) => void | Promise<void>
let unauthorizedHandler: UnauthorizedHandler | null = null

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler
}

async function _maybeNotifyUnauthorized(res: Response): Promise<void> {
  if (!unauthorizedHandler) return
  if (res.status !== 401 && res.status !== 403) return
  try {
    await unauthorizedHandler(res)
  } catch {
    /* never let the handler break the response chain */
  }
}

export async function getAuthToken(): Promise<string | null> {
  if (authTokenGetter) {
    const token = await authTokenGetter()
    cachedAuthToken = token // Keep cache in sync
    return token
  }
  return null
}

// =============================================================================
// Centralized fetch (read this before adding new HTTP calls)
// =============================================================================
// All HTTP calls from the Console MUST go through `apiFetch` (raw Response) or
// `apiRequest` (parsed JSON). They set:
//   - credentials: 'include'   (session affinity / cookies)
//   - Authorization             (Supabase JWT, when available)
//   - Content-Type: application/json (skipped for FormData bodies)
//
// Calling `fetch()` directly will silently drop these defaults — including
// session affinity — so don't. The only legitimate raw `fetch()` users are:
//   - same-origin /dev/api/* dashboard endpoints
//   - asset URL downloads (Supabase signed URLs, blob fetches)
//   - GoTrue AUTH requests (see below)
//
// AUTH EXCEPTION (RFC-1015): `credentials: 'include'` here is an api.*-only rule
// (the DREAMWRIGHT_PROJECT project-affinity cookie). Auth requests do NOT go
// through apiFetch — the in-house GoTrue client (`src/lib/gotrueClient.ts`) owns
// them and MUST use `credentials: 'omit'`. `auth.*` is a different, stateless
// origin (bearer tokens + localStorage, no Set-Cookie) whose LB returns wildcard
// `Access-Control-Allow-Origin: *`, which the browser hard-blocks for a
// credentialed request. Do NOT "unify" the two — the seam is intentional.

export interface ApiFetchOptions extends RequestInit {
  /** Skip the Authorization header (use for unauthenticated endpoints like /auth/signup) */
  skipAuth?: boolean
  /**
   * Synchronous handoff invoked after auth/headers are ready and immediately
   * before the browser fetch starts. Used by durable client command queues to
   * mark a request submitted without opening an await-sized reload race.
   */
  onRequestStart?: () => void
  /**
   * Generic retries are defaulted only for idempotent methods. Admission-only
   * retries a mutation solely after a definitive pre-admission 503.
   */
  retryTransient?: boolean | 'admission-only'
}

/** Header carrying the user's analytics consent to the API (RFC-1577 §8.7). */
const ANALYTICS_CONSENT_HEADER = 'X-Analytics-Consent'

/**
 * Header naming the surface an upload came from, for the API's
 * `asset_upload_result` event (RFC-1577): only the console knows the surface,
 * only the server knows the terminal result.
 */
const UPLOAD_SOURCE_HEADER = 'X-Upload-Source'

function uploadSourceHeaders(source: UploadSource | undefined): Record<string, string> {
  return source ? { [UPLOAD_SOURCE_HEADER]: source } : {}
}

async function buildRequestInit(opts: ApiFetchOptions = {}): Promise<RequestInit> {
  const {
    skipAuth,
    retryTransient: _retryTransient,
    onRequestStart: _onRequestStart,
    ...init
  } = opts
  const headers = new Headers(init.headers)

  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (!skipAuth) {
    const token = await getAuthToken()
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }
  }

  // UI locale is display-only (not auth-gated); attach on every request,
  // including unauthed ones, so backend LocalizedHTTPException handlers can
  // pick the right catalog language. Header, never JWT (S1-1).
  if (!headers.has('X-Dreamwrite-UI-Locale')) {
    headers.set('X-Dreamwrite-UI-Locale', getUiLocale())
  }

  // RFC-1577 §8.7: the server emits analytics events for this request only
  // when the user's cookie choice allows analytics — the same gate the
  // browser's own events use. Sent on every call; no value ⇒ server drops.
  if (!headers.has(ANALYTICS_CONSENT_HEADER)) {
    headers.set(ANALYTICS_CONSENT_HEADER, isAnalyticsAllowed() ? 'granted' : 'denied')
  }

  return {
    ...init,
    credentials: init.credentials ?? 'include',
    headers,
  }
}

/**
 * Generic transient-failure retry policy. Delays in milliseconds before
 * each successive retry attempt. 3 retries total ≈ 7s of backoff.
 *
 * Triggered by:
 *  - Network errors (fetch threw — connection refused, timeout, DNS)
 *  - Any 5xx HTTP response (server-side transient error, including 502
 *    when an instance was killed mid-request, 503 service unavailable,
 *    504 gateway timeout)
 *
 * NOT retried:
 *  - 4xx responses (client error — retry won't fix it)
 *  - Final attempt's failure (returned to caller)
 *
 * Idempotency boundary: apiFetch selects this policy by default only for
 * GET/HEAD/OPTIONS. Mutations never replay ambiguous network or generic 5xx
 * failures; selected command endpoints use the separate admission-only policy
 * below, which recognizes a definitive pre-admission 503.
 */
const TRANSIENT_RETRY_DELAYS_MS: number[] = [1000, 2000, 4000]

class ServiceUnavailableError extends Error {
  constructor(
    public readonly code: string | undefined,
    public readonly retryAfterMs: number | undefined,
    public readonly response: Response,
  ) {
    super(`Service unavailable (${code ?? 'unknown'})`)
    this.name = 'ServiceUnavailableError'
  }
}

export class ApiRequestError extends Error {
  public readonly status: number
  public readonly statusText: string
  public readonly errorData: ApiValue | null
  /** `X-Dreamwrite-Admission` on a pre-admission 503 (`draining`/`not-ready`). */
  public readonly admission: string | null

  constructor(message: string, response: Response, errorData: ApiValue | null) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = response.status
    this.statusText = response.statusText
    this.errorData = errorData
    this.admission = response.headers.get('X-Dreamwrite-Admission')
  }
}

function _sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function _sleepWithAbort(ms: number, signal?: AbortSignal | null): Promise<void> {
  if (!signal) return _sleep(ms)
  if (signal.aborted) {
    return Promise.reject(signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'))
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', onAbort)
      reject(signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'))
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal.addEventListener('abort', onAbort, { once: true })
    if (signal.aborted) onAbort()
  })
}

/**
 * Wrap *fn* with retry on transient failures (network errors + 5xx).
 *
 * Returns:
 *  - The first 2xx/3xx/4xx response (no retry)
 *  - The final response if all retries exhausted (caller decides what
 *    to do — apiRequest raises a typed error from the body)
 *
 * Throws:
 *  - The fetch error from the final attempt if every retry threw
 */
async function withTransientRetry(
  url: string,
  fn: () => Promise<Response>,
  signal?: AbortSignal | null,
): Promise<Response> {
  let attempt = 0
  let lastError: Error | undefined
  while (attempt <= TRANSIENT_RETRY_DELAYS_MS.length) {
    try {
      const res = await fn()
      // Retry on any 5xx — covers 502 (backend killed mid-request),
      // 503 (no healthy backends / draining), 504 (gateway timeout).
      if (res.status >= 500 && res.status < 600) {
        if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
          const delay = TRANSIENT_RETRY_DELAYS_MS[attempt]
          debugTransientRetry({ event: 'retry', status: res.status, attempt: attempt + 1, delay_ms: delay, url })
          await _sleepWithAbort(delay, signal)
          attempt++
          continue
        }
        debugTransientRetry({ event: 'exhausted', status: res.status, url })
        return res
      }
      return res
    } catch (err) {
      // Network error (fetch threw): connection refused, timeout, DNS.
      lastError = err instanceof Error ? err : new Error(String(err))
      if (signal?.aborted) {
        debugTransientRetry({ event: 'aborted', url })
        throw signal.reason ?? err
      }
      if (attempt >= TRANSIENT_RETRY_DELAYS_MS.length) {
        debugTransientRetry({ event: 'exhausted-network', url })
        throw err
      }
      const delay = TRANSIENT_RETRY_DELAYS_MS[attempt]
      debugTransientRetry({ event: 'retry-network', attempt: attempt + 1, delay_ms: delay, url })
      await _sleepWithAbort(delay, signal)
      attempt++
    }
  }
  // Defensive — loop exit without explicit return/throw; rethrow last error
  // if any, otherwise this is unreachable.
  throw lastError
}

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])
const MAX_ADMISSION_RETRY_DELAY_MS = 5_000
const MAX_ADMISSION_RETRY_BUDGET_MS = 60_000

export type ExtractVideoAudioMode = 'raw' | 'isolated_stems' | 'dialogues'

export interface ExtractVideoAudioResult {
  soundNodeIds: string[]
  mode: ExtractVideoAudioMode
  status: 'pending' | 'running' | 'completed'
  jobId?: string
}

export interface AudioSplitStatusResponse {
  jobId: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'cancelled' | string
  error?: string
}

function _admissionRetry(response: Response): {
  retryAfterMs: number
  retryBudgetMs: number
} | null {
  if (response.status !== 503) return null
  const reason = response.headers.get('X-Dreamwrite-Admission')
  if (reason !== 'draining' && reason !== 'not-ready') return null
  const retryAfterSeconds = Number(response.headers.get('Retry-After') ?? '1')
  const retryBudgetSeconds = Number(
    response.headers.get('X-Dreamwrite-Admission-Retry-Budget') ?? '30',
  )
  if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) return null
  if (!Number.isFinite(retryBudgetSeconds) || retryBudgetSeconds <= 0) return null
  return {
    retryAfterMs: Math.min(retryAfterSeconds * 1000, MAX_ADMISSION_RETRY_DELAY_MS),
    retryBudgetMs: Math.min(retryBudgetSeconds * 1000, MAX_ADMISSION_RETRY_BUDGET_MS),
  }
}

async function withAdmissionOnlyRetry(
  url: string,
  fn: () => Promise<Response>,
  signal?: AbortSignal | null,
): Promise<Response> {
  let deadlineMs: number | undefined
  let attempt = 0
  for (;;) {
    // Network errors intentionally propagate: the server may have accepted the
    // mutation, so replay would be ambiguous.
    const response = await fn()
    const admission = _admissionRetry(response)
    if (!admission) return response

    const now = Date.now()
    deadlineMs ??= now + admission.retryBudgetMs
    if (now >= deadlineMs) return response
    const jitterMs = Math.floor(Math.random() * 250)
    const delayMs = Math.min(admission.retryAfterMs + jitterMs, deadlineMs - now)
    if (delayMs <= 0) return response
    attempt += 1
    debugTransientRetry({
      event: 'retry-admission',
      status: response.status,
      attempt,
      delay_ms: delayMs,
      retry_deadline_ms: deadlineMs,
      url,
    })
    await _sleepWithAbort(delayMs, signal)
  }
}

/**
 * Issue an HTTP call to the backend with all shared headers + credentials applied.
 * Returns the raw `Response` — use this for SSE / streaming / non-JSON downloads.
 *
 * `path` may be a relative path (prepended with the configured API base) or an
 * absolute URL (used as-is — useful for already-built URLs).
 *
 * Idempotent methods retry generic transient failures by default. Mutations do
 * not retry ambiguous network/LB failures; callers may opt into the typed
 * admission-only seam for definitive pre-admission 503 responses.
 */
export async function apiFetch(path: string, opts?: ApiFetchOptions): Promise<Response> {
  const apiBase = getApiBase()
  const isAbsoluteUrl = path.startsWith('http://') || path.startsWith('https://')
  const url = isAbsoluteUrl
    ? path
    : `${apiBase}${path}`
  const init = await buildRequestInit(opts)
  // Keep this callback adjacent to fetch. JavaScript runs both statements in
  // the same task, so page teardown cannot land between a persisted command
  // being marked submitted and the browser receiving its keepalive request.
  opts?.onRequestStart?.()
  const retryMode = opts?.retryTransient
  const method = (init.method ?? 'GET').toUpperCase()
  let response: Response
  if (retryMode === 'admission-only') {
    response = await withAdmissionOnlyRetry(url, () => fetch(url, init), init.signal)
  } else if (retryMode === true || (retryMode === undefined && IDEMPOTENT_METHODS.has(method))) {
    response = await withTransientRetry(url, () => fetch(url, init), init.signal)
  } else {
    response = await fetch(url, init)
  }
  // Notify the global handler (account-self-service) — fire-and-forget.
  if (response.status === 401 || response.status === 403) {
    void _maybeNotifyUnauthorized(response)
  }
  return response
}

export { ServiceUnavailableError }

/**
 * Issue an HTTP call and parse the JSON response. Throws on non-2xx with the
 * extracted error message. Handles 204 (No Content) and empty bodies as `undefined`.
 */
export async function apiRequest<T>(path: string, opts?: ApiFetchOptions): Promise<T> {
  const response = await apiFetch(path, opts)

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new ApiRequestError(
      extractErrorMessage(errorData, response.statusText),
      response,
      errorData,
    )
  }

  if (response.status === 204) {
    return undefined!
  }

  const contentType = response.headers.get('content-type')
  if (contentType && !contentType.includes('application/json')) {
    if (!getApiBase()) {
      throw new Error(
        'API endpoint not configured. Click the Settings icon (gear) in the header to set your API URL.',
      )
    }
    throw new Error(
      `API returned unexpected content type: ${contentType}. Check your API endpoint configuration in Settings.`,
    )
  }

  // Some endpoints (DELETE etc.) return 200 with an empty body — treat as undefined.
  const text = await response.text()
  return text ? parseJsonResponse<T>(text) : undefined!
}

/**
 * @deprecated Use {@link apiFetch} instead. Kept as a thin shim for legacy
 * callers (CanvasToolbox, useNodeActions, ImageHistoryPopover, useClipPreloader,
 * useClipThumbnails, scriptWriterClient). Delegating preserves their existing
 * call sites while inheriting credentials + auth automatically.
 */
export async function authenticatedFetch(url: string, init?: RequestInit): Promise<Response> {
  return apiFetch(url, init)
}

export function getCachedAuthToken(): string | null {
  return cachedAuthToken
}

function getApiBase(): string {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return getDefaultApiBase()
    }
    return localStorage.getItem(API_BASE_KEY) || getDefaultApiBase()
  }
  return getDefaultApiBase()
}

export function setApiBase(url: string): void {
  if (url) {
    localStorage.setItem(API_BASE_KEY, url)
  } else {
    localStorage.removeItem(API_BASE_KEY)
  }
}

export function getConfiguredApiBase(): string {
  return getApiBase()
}

/**
 * Build a project asset URL. If the path is already an absolute URL
 * (e.g. a signed Supabase URL from a published snapshot), returns it as-is.
 */
export function buildProjectAssetUrl(projectId: string, assetPath: string): string {
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  return `${getApiBase()}/projects/${projectId}/${assetPath}`
}

// Video Model management (veo or wan)
export type VideoModel = 'veo' | 'wan_flf2v'

export interface VideoModelCapabilities {
  model: VideoModel
  label: string
  durations_seconds: number[]
  resolutions: Array<'480p' | '720p' | '1080p'>
  supports_interpolation: boolean
  interpolation_requires_duration_seconds?: number | null
  requires_end_frame: boolean
}

export interface VideoModelsResponse {
  models: VideoModelCapabilities[]
}

// RFC-0856 I7: feature-flag admin DTOs (hand-written REST shapes, matching the
// convention of PricingItem/Project etc. — not codegen'd node data).
export type FlagKind = 'flag' | 'config'
export type FlagValueType = 'bool' | 'string' | 'number' | 'object'

export interface FlagListItem {
  key: string
  kind: FlagKind
  value_type: FlagValueType
  sensitive: boolean
  // Raw stored value when is_set, else the registry default. `value` here is
  // "dangerous to change", NOT redacted — sensitive gates a confirm, not masking.
  value: ApiValue
  version: number | null
  updated_by: string | null
  updated_at: string | null
  is_set: boolean
}

// Machine codes in the backend's `{detail:{code,message,expected?,actual?}}`.
export type FlagErrorCode =
  | 'FLAG_NOT_REGISTERED'
  | 'FLAG_TARGETABLE_UNSUPPORTED'
  | 'FLAG_VALUE_TYPE_MISMATCH'
  | 'FLAG_VALUE_NOT_JSON'
  | 'FLAG_BODY_NOT_OBJECT'
  | 'FLAG_EXPECTED_VERSION_INVALID'
  | 'FLAG_CONFIRM_REQUIRED'
  | 'FLAG_VERSION_CONFLICT'
  | 'FLAG_LOCK_TIMEOUT'

export interface PricingItem {
  model_id: string
  model_provider: string
  operation_type: string
  sparks_per_unit: number
  unit_type: string
  display_name: string
}

export type DirectorModel = string

export interface DirectorModelInfo {
  id: DirectorModel
  label: string
  description?: string | null
  enabled: boolean
  is_default: boolean
  source?: 'native' | 'byom'
  config_id?: string | null
}

export interface DirectorModelsResponse {
  allow_internal_models: boolean
  models: DirectorModelInfo[]
  default_model: DirectorModel
  selected_model: DirectorModel
}

export interface DirectorModelSelectionResponse {
  selected_model: DirectorModel
}

export function getDirectorModel(): DirectorModel {
  if (typeof window !== 'undefined') {
    const model = localStorage.getItem(DIRECTOR_MODEL_KEY)
    return model?.trim() || 'default'
  }
  return 'default'
}

export function setDirectorModel(model: DirectorModel): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(DIRECTOR_MODEL_KEY, model)
  }
}

/**
 * Resolve the user's ACTIVE UI locale for the X-Dreamwrite-UI-Locale header so
 * the backend can localize LocalizedHTTPException detail text.
 *
 * Prefers the live i18next language (`i18n.language`), which is the single
 * source of truth reflecting the full detector chain — localStorage →
 * navigator, reconciled after auth against the backend-owned
 * user_preferred_locales table by LocaleSync. This matches how the rest of the
 * app reads the active locale (`normalizeLocale(i18n.language)`), so the header
 * can't lag the visible UI language when `dw_preferred_locale` is missing/stale
 * at boot. Falls back to the persisted `LOCALE_STORAGE_KEY` value, then 'en'.
 *
 * Never throws: all storage / i18n access is guarded, because this runs on the
 * hot path of every request and `localStorage` access can throw (e.g.
 * SecurityError with cookies/storage blocked) — a failure here must degrade to
 * 'en', not break the fetch.
 */
export function getUiLocale(): SupportedLocale {
  if (typeof window === 'undefined') return 'en'
  try {
    const active = normalizeLocale(i18n.language)
    if (active) return active
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY)
    return normalizeLocale(raw) ?? 'en'
  } catch {
    return 'en'
  }
}

export function getVideoModel(): VideoModel {
  if (typeof window !== 'undefined') {
    const model = localStorage.getItem(VIDEO_MODEL_KEY)
    // Back-compat: treat legacy "wan" as "wan_flf2v"
    return model === 'wan_flf2v' || model === 'wan' ? 'wan_flf2v' : 'veo'
  }
  return 'veo'
}

export function setVideoModel(model: VideoModel): void {
  localStorage.setItem(VIDEO_MODEL_KEY, model)
}

// Video Simulator Mode management (returns placeholder videos instead of calling real APIs)
export function getVideoSimulatorMode(): boolean {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(VIDEO_SIMULATOR_MODE_KEY) === 'true'
  }
  return false
}

export function setVideoSimulatorMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(VIDEO_SIMULATOR_MODE_KEY, 'true')
  } else {
    localStorage.removeItem(VIDEO_SIMULATOR_MODE_KEY)
  }
}


/**
 * Extract error message from API error response.
 * Handles different error formats:
 * 1. FastAPI's {detail: string}
 * 2. Our structured error {error: {message: string}}
 * 3. Dependency error {detail: {error: {...}, missing_dependencies: [...]}}
 */
function extractErrorMessage(errorData: ApiValue | null, fallback: string): string {
  if (!isApiRecord(errorData)) {
    return fallback
  }
  const data = errorData

  // Check for detail field
  const detailValue = data.detail
  if (typeof detailValue === 'string') {
    return detailValue
  }

  // Check for nested error in detail (dependency errors, etc.)
  if (isApiRecord(detailValue)) {
    const detail = detailValue
    // Dependency errors may come back as:
    // { detail: { detail: string, missing_dependencies: [...] } }
    const nestedDetail = stringField(detail, 'detail')
    if (nestedDetail) {
      const missingDeps = Array.isArray(detail.missing_dependencies) ? detail.missing_dependencies : null
      if (missingDeps && missingDeps.length > 0) {
        // Try to extract readable messages if present.
        const depMessages = missingDeps
          .map((dep) => {
            if (!isApiRecord(dep)) return null
            if (typeof dep.message === 'string') return dep.message
            if (typeof dep.type === 'string') return dep.type
            return null
          })
          .filter(Boolean)
          .slice(0, 5)
        if (depMessages.length > 0) {
          return `${nestedDetail}\n\nMissing:\n- ${depMessages.join('\n- ')}`
        }
      }
      return nestedDetail
    }
    if (isApiRecord(detail.error)) {
      return stringField(detail.error, 'message') ?? fallback
    }
    if (typeof detail.error === 'string') {
      return detail.error
    }
    // Structured single-error detail: { detail: { code, message } } — used by
    // e.g. /admin/sparks/topup (TOPUP_CAP_EXCEEDED / USER_NOT_FOUND). Surface
    // the provider/route reason verbatim instead of a generic status text.
    const directMessage = stringField(detail, 'message')
    if (directMessage) {
      return directMessage
    }
  }

  // Check for top-level error object
  if (isApiRecord(data.error)) {
    return stringField(data.error, 'message') ?? fallback
  }

  return fallback
}

/** Validated result of `POST /projects/{id}/assets/annotate`. */
export interface AnnotateAssetResponse {
  path: string
  filename: string
  size: number
  aspect_ratio?: string | null
  width?: number | null
  height?: number | null
}

/**
 * The response as it arrives, before validation. `apiRequest<T>` does not check
 * the payload against `T`, so every field is treated as absent until proven
 * otherwise by `parseAnnotateAssetResponse`.
 */
interface RawAnnotateAssetResponse {
  path?: string
  filename?: string
  size?: number
  aspect_ratio?: string | null
  width?: number | null
  height?: number | null
}

/**
 * Runtime guard for the annotate response.
 *
 * The save path writes `path` straight into `out_image` on a new node, so a
 * malformed response would persist a broken asset ref into YDoc and only surface
 * later as a node that renders nothing. Failing here keeps the bad value out of
 * the document (Dev Principle 2).
 */
function parseAnnotateAssetResponse(payload: RawAnnotateAssetResponse): AnnotateAssetResponse {
  const { path, filename, size } = payload
  if (typeof path !== 'string' || path === '') {
    throw new Error('Annotate response is missing a usable asset path.')
  }
  if (typeof filename !== 'string' || filename === '') {
    throw new Error('Annotate response is missing a filename.')
  }
  if (typeof size !== 'number') {
    throw new Error('Annotate response is missing a size.')
  }
  return {
    path,
    filename,
    size,
    aspect_ratio: payload.aspect_ratio,
    width: payload.width,
    height: payload.height,
  }
}

/**
 * One row of `GET /auth/api-keys`. Mirrors the backend `ApiKeyListItem`
 * response model — masked prefix only, never the raw key (RFC-0822 / CWE-312).
 * Exported so consumers share this contract instead of restating it.
 */
export interface ApiKeyListItem {
  id: string
  key_prefix: string
  name: string
  created_at: string
  last_used_at: string | null
}

/** Response of `extractDocumentText` (RFC-1218 PR1) — text only, no asset. */
export interface DocumentTextExtraction {
  filename: string
  kind: 'PDF' | 'DOCX' | 'PPTX' | 'TXT' | 'MD'
  text: string
  char_count: number
  segment_count: number
  /**
   * RFC-1235 PR2 — embedded images that passed CSAM screening. A document only
   * reaches this response if EVERY screenable image it holds passed; one
   * failure rejects the whole upload, so this is never a partial count.
   */
  cleared_image_count: number
  /** Images with no screenable content (sub-32px furniture, vector metafiles). */
  skipped_image_count: number
  /** False when integrity screening is off; images are then not surfaced at all. */
  images_screened: boolean
}

/**
 * Query string carrying the tab scope, or '' for the pre-teams request.
 *
 * Module-level rather than a method: `private` is erased at runtime, so a
 * private method still lands on the prototype and the shared api mock has to
 * grow a stub for it (apiClientMockParity.smoke.test.ts). This holds no state.
 */
export function tabScopeQuery(scope?: TabScope): string {
  if (!scope?.teamId) return ''
  const qs = new URLSearchParams({ team_id: scope.teamId, visibility: scope.visibility })
  return `?${qs.toString()}`
}

class ApiClient {
  private request<T>(path: string, options?: ApiFetchOptions): Promise<T> {
    return apiRequest<T>(path, options)
  }

  // API Keys (per-user, server-managed)
  async createApiKey(name: string): Promise<{ id: string; key: string; key_prefix: string; name: string }> {
    return this.request('/auth/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  async listApiKeys(): Promise<ApiKeyListItem[]> {
    return this.request('/auth/api-keys')
  }

  async revokeApiKey(keyId: string): Promise<void> {
    return this.request(`/auth/api-keys/${keyId}`, { method: 'DELETE' })
  }

  // Projects
  async listProjects(
    limit = 50,
    offset = 0,
    teamId?: string | null,
  ): Promise<PaginatedResponse<ProjectListItem>> {
    // team_id is OMITTED, not sent empty, when there is no active team — the
    // server treats absence as pre-teams behaviour (everything you own), which
    // is exactly what the dashboard needs while the flag is off.
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (teamId) qs.set('team_id', teamId)
    return this.request(`/projects?${qs.toString()}`)
  }

  async getProject(projectId: string): Promise<Project> {
    return this.request(`/projects/${projectId}/full`)
  }

  async createProject(request: CreateProjectRequest): Promise<CreateProjectResponse> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async deleteProject(projectId: string): Promise<void> {
    await apiRequest<void>(`/projects/${projectId}`, { method: 'DELETE' })
  }

  async cloneProject(projectId: string, name?: string): Promise<{ id: string; name: string; message: string }> {
    return this.request(`/projects/${projectId}/clone`, {
      method: 'POST',
      body: JSON.stringify(name ? { name } : {}),
    })
  }

  async updateProject(
    projectId: string,
    data: { name?: string; status?: ProjectStatus; has_celebrity?: boolean }
  ): Promise<ProjectListItem> {
    return this.request(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async starProject(projectId: string): Promise<void> {
    await apiFetch(`/projects/${projectId}/star`, { method: 'POST' })
  }

  async unstarProject(projectId: string): Promise<void> {
    await apiFetch(`/projects/${projectId}/star`, { method: 'DELETE' })
  }

  /**
   * Read the per-canvas workflow config (style / aspect ratio / production target).
   * Backed by WorkflowState (stored per canvas); surfaced under the canvas gear
   * menu. `canvasId` scopes the read to the active canvas; omitting it (or passing
   * the default canvas) targets the backend default canvas unchanged.
   */
  async getProjectConfig(projectId: string, canvasId?: string): Promise<ProjectConfig> {
    return this.request(`/projects/${projectId}/agentic/config${canvasConfigQuery(canvasId)}`)
  }

  /**
   * Patch per-canvas workflow config. All fields optional; each is independently
   * applied via the orchestrator setters. Existing canvas nodes are unaffected.
   * `canvasId` scopes the write to the active canvas.
   */
  async updateProjectConfig(
    projectId: string,
    data: UpdateProjectConfigRequest,
    canvasId?: string,
  ): Promise<ProjectConfig> {
    return this.request(`/projects/${projectId}/agentic/config${canvasConfigQuery(canvasId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Assets
  getAssetUrl(
    projectId: string,
    assetPath: string,
    options?: { width?: number; bustCache?: string; poster?: boolean }
  ): string {
    // Full URLs (e.g. Supabase public URLs from upload nodes) are used as-is
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
      return assetPath
    }

    const baseUrl = getApiBase()
    // Asset paths may already include 'assets/' prefix
    let url: string
    if (assetPath.startsWith('assets/')) {
      url = `${baseUrl}/projects/${projectId}/${assetPath}`
    } else {
      url = `${baseUrl}/projects/${projectId}/assets/${assetPath}`
    }
    // Build query params
    const params = new URLSearchParams()
    if (options?.width) {
      params.set('w', String(options.width))
    }
    if (options?.bustCache) {
      params.set('v', options.bustCache)
    }
    // Request the pre-generated first-frame poster sibling of a video (RFC-0931).
    if (options?.poster) {
      params.set('poster', '1')
    }
    // Include auth token for <img> tags (can't use Authorization header)
    const token = getCachedAuthToken()
    if (token) {
      params.set('token', token)
    }
    // Include publication token for unauthenticated access to published project assets
    if (currentPublicationToken) {
      params.set('publication_token', currentPublicationToken)
    }
    const queryString = params.toString()
    if (queryString) {
      url += `?${queryString}`
    }
    return url
  }

  // Convenience method for thumbnails
  getThumbnailUrl(projectId: string, assetPath: string, width: number = 200, bustCache?: string): string {
    return this.getAssetUrl(projectId, assetPath, { width, bustCache })
  }

  // Get a library asset URL for thumbnails of library-sourced nodes
  getLibraryAssetUrl(assetPath: string): string {
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
      return assetPath
    }
    const baseUrl = getApiBase()
    const clean = assetPath.startsWith('assets/') ? assetPath.slice(7) : assetPath
    let url = `${baseUrl}/library/assets/${clean}`
    const token = getCachedAuthToken()
    if (token) {
      url += `?token=${encodeURIComponent(token)}`
    }
    return url
  }

  // Upload an asset file (image, video, audio) through the backend API.
  // `source` is the surface the file came from (analytics only).
  async uploadAsset(
    projectId: string,
    file: File,
    name?: string,
    source?: UploadSource,
  ): Promise<{
    path: string
    filename: string
    size: number
    // Present for image uploads; snapped to a canonical AspectRatio bucket.
    aspect_ratio?: string | null
    width?: number | null
    height?: number | null
    // Present for video uploads (ffprobe container duration, seconds).
    duration_seconds?: number | null
  }> {
    const mediaType = uploadMediaTypeFromMime(file.type)
    const sizeError = mediaType ? getFileSizeError(file.size, mediaType) : null
    if (mediaType && sizeError) {
      assetUploadLog({
        event: 'file-size-rejected-before-request',
        project_id: projectId,
        media_type: mediaType,
        file_size_bytes: file.size,
        max_size_bytes: MAX_FILE_SIZE[mediaType],
      })
      throw new Error(
        i18n.t(`canvas:nodeError.${sizeError.code}`, { ...sizeError.params }),
      )
    }

    const formData = new FormData()
    formData.append('file', file)
    if (name) formData.append('name', name)
    return apiRequest(`/projects/${projectId}/assets/upload`, {
      method: 'POST',
      body: formData,
      headers: uploadSourceHeaders(source),
    })
  }

  /**
   * Read a PDF/DOCX/PPTX and return its text for use as prompt context (RFC-1218 PR1).
   *
   * Deliberately NOT `uploadAsset`: a document is context, not media. The
   * backend parses it in-request and keeps nothing — no asset path, no node,
   * and no stored bytes, because embedded images are not CSAM-scanned until
   * PR2. There is nothing to clean up if the user removes the attachment.
   *
   * Rejections are specific and worth surfacing verbatim: 415 unsupported
   * type, 413 too large, 422 unreadable or no text layer (a scan).
   */
  async extractDocumentText(
    projectId: string,
    file: File,
    source?: UploadSource,
  ): Promise<DocumentTextExtraction> {
    const formData = new FormData()
    formData.append('file', file)
    return apiRequest(`/projects/${projectId}/documents/extract-text`, {
      method: 'POST',
      body: formData,
      headers: uploadSourceHeaders(source),
    })
  }

  /**
   * Same extraction, without a project — the landing composer attaches a
   * document BEFORE the project is created, so it has no id to scope to.
   * Authentication is the access boundary; the request persists nothing.
   */
  async extractLandingDocumentText(file: File): Promise<DocumentTextExtraction> {
    const formData = new FormData()
    formData.append('file', file)
    return apiRequest('/documents/extract-text', {
      method: 'POST',
      body: formData,
    })
  }

  /**
   * Bake vector markup into the raw pixels of an existing project asset.
   *
   * Returns a NEW asset path — the source is never mutated, and the route
   * deliberately does not create a node. Wiring the result into the graph is the
   * caller's job, because structural graph changes belong to the console's
   * direct-to-YDoc path (and to `propose_dag_changes` on the Director side).
   * See RFC-1115 §2.
   *
   * The backend route is always live. Frontend exposure is limited to internal
   * accounts, but provider/storage/validation errors still surface verbatim.
   */
  async annotateAsset(
    projectId: string,
    sourceAssetPath: string,
    annotations: ImageAnnotationDict[],
  ): Promise<AnnotateAssetResponse> {
    const payload: RawAnnotateAssetResponse = await apiRequest(
      `/projects/${projectId}/assets/annotate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_asset_path: sourceAssetPath, annotations }),
      },
    )
    return parseAnnotateAssetResponse(payload)
  }

  /** Create a depth result with an explicit audio choice; omitted choices remain silent. */
  async createDepthVideo(
    projectId: string,
    sourceNodeId: string,
    canvasId: string,
    includeAudio = false,
  ): Promise<{ nodeId: string; status: string }> {
    const query = new URLSearchParams({ canvas_id: canvasId }).toString()
    const payload = await apiRequest<{
      success: boolean
      result?: {
        success?: boolean
        node_id?: string
        status?: string
        error?: string
      }
      error?: string
    }>(`/projects/${projectId}/agentic/director/tools/create_depth_video?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_node_id: sourceNodeId, include_audio: includeAudio }),
    })
    const result = payload.result
    if (!payload.success || !result?.success || !result.node_id) {
      throw new Error(result?.error || payload.error || 'Depth video creation failed')
    }
    return { nodeId: result.node_id, status: result.status || 'scheduled' }
  }

  async createUpscaleVideo(
    projectId: string,
    sourceNodeId: string,
    canvasId: string,
    name: string,
    targetResolution: UpscaleTargetResolution,
  ): Promise<{ nodeId: string; status: string }> {
    const query = new URLSearchParams({ canvas_id: canvasId }).toString()
    const payload: ApiValue = await apiRequest<ApiValue>(`/projects/${projectId}/agentic/director/tools/create_upscale_video?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_node_id: sourceNodeId,
        name,
        target_resolution: targetResolution,
      }),
    })
    const result = isApiRecord(payload) && isApiRecord(payload.result) ? payload.result : undefined
    const resultError = result ? stringField(result, 'error') : undefined
    const payloadError = isApiRecord(payload) ? stringField(payload, 'error') : undefined
    if (
      !isApiRecord(payload) ||
      payload.success !== true ||
      !result ||
      result.success !== true ||
      typeof result.node_id !== 'string' ||
      result.node_id.length === 0 ||
      (result.status !== undefined && typeof result.status !== 'string')
    ) {
      throw new Error(resultError || payloadError || 'HD video creation failed')
    }
    return { nodeId: result.node_id, status: result.status ?? 'scheduled' }
  }

  /**
   * Extract one frame of a video node into a completed Image Upload node via
   * the backend ffmpeg extractor. `frame` is 'first', 'last', or
   * `time:<seconds>` for the playhead.
   */
  async extractVideoFrame(
    projectId: string,
    sourceNodeId: string,
    canvasId: string,
    frame: string,
  ): Promise<{ imageNodeId: string; frame: string }> {
    const query = new URLSearchParams({ canvas_id: canvasId }).toString()
    const payload: ApiValue = await apiRequest<ApiValue>(
      `/projects/${projectId}/agentic/nodes/${encodeURIComponent(sourceNodeId)}/extract-frame?${query}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ frame }),
      },
    )
    const result = isApiRecord(payload) && isApiRecord(payload.result) ? payload.result : undefined
    const resultError = result ? stringField(result, 'error') : undefined
    const payloadError = isApiRecord(payload) ? stringField(payload, 'error') : undefined
    const imageNodeId = result ? stringField(result, 'image_node_id') : undefined
    if (!isApiRecord(payload) || payload.success !== true || !result || result.success !== true || !imageNodeId) {
      throw new Error(resultError || payloadError || 'Frame extraction failed')
    }
    return { imageNodeId, frame: stringField(result, 'frame') ?? frame }
  }

  /**
   * Extract audio from a video node into completed Audio node(s).
   * `raw` creates one node. `isolated_stems` creates vocals and background nodes.
   * `dialogues` creates one isolated vocals node per dialogue segment.
   */
  async extractVideoAudioToSoundExpert(
    projectId: string,
    sourceNodeId: string,
    canvasId: string,
    mode: ExtractVideoAudioMode,
    splitJobId?: string,
  ): Promise<ExtractVideoAudioResult> {
    const query = new URLSearchParams({ canvas_id: canvasId }).toString()
    const payload: ApiValue = await apiRequest<ApiValue>(
      `/projects/${projectId}/agentic/nodes/${encodeURIComponent(sourceNodeId)}/extract-audio?${query}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, split_job_id: splitJobId }),
      },
    )
    const result = isApiRecord(payload) && isApiRecord(payload.result) ? payload.result : undefined
    const resultError = result ? stringField(result, 'error') : undefined
    const payloadError = isApiRecord(payload) ? stringField(payload, 'error') : undefined
    const soundNodeIds = result?.sound_node_ids
    const status = result ? stringField(result, 'status') : undefined
    const jobId = result ? stringField(result, 'job_id') : undefined
    if (
      !isApiRecord(payload) ||
      payload.success !== true ||
      !result ||
      result.success !== true ||
      (status !== 'pending' && status !== 'running' && status !== 'completed') ||
      !Array.isArray(soundNodeIds) ||
      !soundNodeIds.every((nodeId) => typeof nodeId === 'string' && nodeId.length > 0) ||
      ((status === 'pending' || status === 'running') && !jobId)
    ) {
      throw new Error(resultError || payloadError || 'Audio extraction failed')
    }
    return {
      soundNodeIds,
      mode: stringField(result, 'mode') === 'isolated_stems'
        ? 'isolated_stems'
        : stringField(result, 'mode') === 'dialogues'
          ? 'dialogues'
          : 'raw',
      status,
      jobId,
    }
  }

  async getAudioSplitStatus(projectId: string, jobId: string): Promise<AudioSplitStatusResponse> {
    return apiRequest<AudioSplitStatusResponse>(
      `/projects/${projectId}/audio-split/${encodeURIComponent(jobId)}`,
    )
  }

  async createAiClips(
    projectId: string,
    sourceNodeId: string,
    canvasId: string,
  ): Promise<{ groupId: string; clipNodeIds: string[]; sceneCount: number }> {
    const query = new URLSearchParams({ canvas_id: canvasId }).toString()
    const payload = await apiRequest<{
      success: boolean
      result?: {
        success?: boolean
        group_id?: string
        clip_node_ids?: string[]
        scene_count?: number
        error?: string
      }
      error?: string
    }>(`/projects/${projectId}/agentic/director/tools/create_ai_clips?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_node_id: sourceNodeId }),
    })
    const result = payload.result
    if (
      !payload.success ||
      !result?.success ||
      typeof result.group_id !== 'string' ||
      result.group_id.length === 0 ||
      !Array.isArray(result.clip_node_ids) ||
      !result.clip_node_ids.every((nodeId) => typeof nodeId === 'string' && nodeId.length > 0) ||
      typeof result.scene_count !== 'number' ||
      !Number.isInteger(result.scene_count) ||
      result.scene_count <= 0 ||
      result.clip_node_ids.length !== result.scene_count
    ) {
      throw new Error(result?.error || payload.error || 'Rough Cut creation failed')
    }
    return {
      groupId: result.group_id,
      clipNodeIds: result.clip_node_ids,
      sceneCount: result.scene_count,
    }
  }

  // Sparks (credits)
  // `balance` is SPENDABLE (min of wallet and any per-member cap) and is what
  // the pre-flight gate compares against. `pool` is the wallet's own balance
  // and is what the canvas badge shows, so a capped member is not told the team
  // is nearly out. `allowance_remaining` is null when uncapped.
  async getSparks(
    projectId?: string,
  ): Promise<{
    balance: number
    pool?: number | null
    allowance_remaining?: number | null
  }> {
    // RFC-1372 C2: the paying wallet is derived from the PROJECT, so a balance
    // shown on a project surface has to name the project or it can report a
    // different wallet than the one about to be charged. Omitted elsewhere
    // (e.g. settings), where "your own wallet" is the right question.
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''
    return this.request(`/sparks${qs}`)
  }

  // Billing catalog (@meta.com-gated server-side). Decoded + validated rather
  // than trusting the generic: the wire payload is snake_case and untrusted,
  // and a silently-coerced price would surface as a wrong number on a
  // purchase button.
  async getCheckoutCountries(signal?: AbortSignal): Promise<CheckoutCountries> {
    return decodeCheckoutCountries(await this.request<ApiValue>('/billing/checkout-countries', { signal }))
  }

  async getBillingCatalog(quote: CheckoutCountryRequest = {}, signal?: AbortSignal): Promise<BillingCatalog> {
    const query = new URLSearchParams()
    if (quote.timeZone !== undefined) query.set('time_zone', quote.timeZone)
    if (quote.countryCode !== undefined) query.set('country_code', quote.countryCode)
    const payload: ApiValue = await this.request<ApiValue>(`/billing/catalog?${query}`, { signal })
    return decodeBillingCatalog(payload)
  }

  // Start a purchase (@meta.com-gated server-side, and live-flag gated).
  //
  // Sends the item, wallet destination and country/time-zone hints. The price, credit grant and
  // end_user reference are all resolved server-side — a client-supplied any of
  // those would be either a self-service discount or, in the case of end_user,
  // a way to touch someone else's orders.
  //
  // The returned `handoffCode` is a single-use credential. Hand it straight to
  // the bootstrap form; never store it.
  async createCheckoutHandoff(itemId: string, destination: CheckoutDestination = { kind: 'personal', teamId: null }, quote: CheckoutCountryRequest = {}): Promise<CheckoutHandoff> {
    const payload: ApiValue = await this.request<ApiValue>('/billing/checkout/handoff', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        destination: destination.kind === 'team' ? { kind: 'team', team_id: destination.teamId } : { kind: 'personal' },
        time_zone: quote.timeZone,
        country_code: quote.countryCode,
      }),
    })
    return decodeHandoff(payload)
  }

  // OUR record of an order, scoped server-side to the caller. Reports what we
  // know; it never asserts that a payment succeeded.
  async getCheckoutOrder(orderId: string, signal?: AbortSignal): Promise<CheckoutOrder> {
    const payload: ApiValue = await this.request<ApiValue>(
      `/billing/checkout/orders/${encodeURIComponent(orderId)}`,
      { signal },
    )
    return decodeCheckoutOrder(payload)
  }

  // Operator-only: mint credits FOR a user by email (T276405916). There is no
  // self-serve mint — the backend gates this on the topup_operator permission
  // (POST /admin/sparks/topup) and enforces the rolling-24h per-recipient cap.
  //
  // We mint a stable idempotency key per call so the network/5xx retry wrapper
  // (apiRequest) can never double-mint: every internal retry of THIS submit
  // reuses the same key, and the backend's idempotency guard collapses them to a
  // single credit. (A new explicit click is a new key = a new intended mint.)
  //
  // `targetTeamId` credits that team's POOLED wallet instead of the
  // recipient's own. The email stays required either way: the rolling-24h cap
  // is per recipient user rather than per wallet, so the operator names whose
  // cap is being spent instead of the server inferring a member.
  // ---------------------------------------------------------------------
  // White-glove onboarding (/admin/onboarding, onboarding_operator gate).
  //
  // One request advances ONE step. There is deliberately no "run everything"
  // call: creating an account, minting credits and mailing an external
  // partner are three decisions, and the server enforces the ordering in the
  // step claim rather than trusting the UI.
  // ---------------------------------------------------------------------

  async onboardingRecipientHistory(email: string): Promise<import('./onboardingTypes').OnboardingRecipientHistory> {
    return this.request('/admin/onboarding/recipient-history', {
      method: 'POST', body: JSON.stringify({ email }),
    })
  }

  async onboardingRecipientHighlights(emails: string[]): Promise<import('./onboardingTypes').OnboardingRecipientHistory[]> {
    return this.request('/admin/onboarding/recipient-highlights', {
      method: 'POST', body: JSON.stringify({ emails }),
    })
  }

  async createOnboardingIntake(request: import('./onboardingTypes').OnboardingIntakeRequest): Promise<import('./onboardingTypes').OnboardingIntakeResult> {
    const [first, ...remaining] = request.targets
    if (!first) throw new Error('Onboarding intake requires at least one target')
    const body: import('./generated/onboardingIntake').CreateIntakeRequest = {
      reason: request.reason, targets: [first, ...remaining],
      validate_only: request.validateOnly, shared_team: request.sharedTeam,
      acknowledged_prior_targets: request.acknowledgedPriorTargets,
    }
    return this.request('/admin/onboarding/runs/intake', {
      method: 'POST', body: JSON.stringify(body),
    })
  }

  async createOnboardingRun(
    reason: string,
    targets: OnboardingRunTargetInput[],
    allowRepeatRecipients = false,
    acknowledgedPriorTargets: Record<string, string> = {},
  ): Promise<OnboardingRun> {
    return this.request('/admin/onboarding/runs', {
      method: 'POST',
      body: JSON.stringify({
        reason,
        targets,
        allow_repeat_recipients: allowRepeatRecipients,
        acknowledged_prior_targets: acknowledgedPriorTargets,
      }),
    })
  }

  /**
   * Validate (and unless `validateOnly`, create) a bulk run from CSV text.
   * All-or-nothing: a 422 CSV_INVALID carries every problem and created nothing
   * — read it with `parseOnboardingCsvInvalid`.
   */
  async createOnboardingCsvRun(request: OnboardingCsvRequest): Promise<OnboardingCsvResult> {
    return this.request('/admin/onboarding/runs/csv', {
      method: 'POST',
      body: JSON.stringify({
        reason: request.reason,
        csv_text: request.csvText,
        validate_only: request.validateOnly,
        allow_repeat_recipients: request.allowRepeatRecipients,
        credit_ceiling_override: request.creditCeilingOverride,
        acknowledged_prior_targets: request.acknowledgedPriorTargets ?? {},
      }),
    })
  }

  /**
   * Advance one step and get back ONLY that target (bulk loop, plan B3).
   *
   * Returns the full run instead if the server predates `?response=target`
   * (mid-rollout); the caller tells them apart by shape.
   */
  async advanceOnboardingTarget(
    runId: string,
    targetId: string,
    step: OnboardingStepName,
    options: OnboardingAdvanceOptions = {},
    targetIds?: string[],
  ): Promise<OnboardingTarget | OnboardingRun> {
    const result = await this.request<OnboardingTarget | OnboardingRun>(
      `/admin/onboarding/runs/${runId}/targets/${targetId}/steps/${step}${onboardingScopeQuery(targetIds, true)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          allow_whole_domain: options.allowWholeDomain ?? false,
          allow_org_move: options.allowOrgMove ?? false,
          expected_current_org: options.expectedCurrentOrg ?? null,
        }),
      },
    )
    if (targetIds !== undefined && (!('target_id' in result) || result.target_id.toLowerCase() !== targetId.toLowerCase())) {
      throw new Error('Scoped advance requires the requested target response')
    }
    return result
  }

  /** Recover an interrupted claim; refused unless its executor is verifiably gone. */
  async recoverOnboardingStep(
    runId: string,
    targetId: string,
    step: OnboardingStepName,
    targetIds?: string[],
  ): Promise<OnboardingRun> {
    const run = await this.request<OnboardingRun>(
      `/admin/onboarding/runs/${runId}/targets/${targetId}/steps/${step}/recover${onboardingScopeQuery(targetIds)}`,
      { method: 'POST' },
    )
    return validateScopedOnboardingRun(run, targetIds)
  }

  async getOnboardingRun(runId: string, targetIds?: string[]): Promise<OnboardingRun> {
    const run = await this.request<OnboardingRun>(`/admin/onboarding/runs/${runId}${onboardingScopeQuery(targetIds)}`)
    return validateScopedOnboardingRun(run, targetIds)
  }

  async getOnboardingPreflight(runId: string, targetIds?: string[]): Promise<OnboardingPreflight> {
    const report = await this.request<OnboardingPreflight>(`/admin/onboarding/runs/${runId}/preflight${onboardingScopeQuery(targetIds)}`)
    const ids = normalizeOnboardingScope(targetIds)
    if (ids && report.targets.some((target) => !ids.includes(target.target_id.toLowerCase()))) {
      throw new Error('Server returned an invalid onboarding preflight scope')
    }
    return report
  }

  async advanceOnboardingStep(
    runId: string,
    targetId: string,
    step: OnboardingStepName,
    options: OnboardingAdvanceOptions = {},
    targetIds?: string[],
  ): Promise<OnboardingRun> {
    const run = await this.request<OnboardingRun>(
      `/admin/onboarding/runs/${runId}/targets/${targetId}/steps/${step}${onboardingScopeQuery(targetIds)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          allow_whole_domain: options.allowWholeDomain ?? false,
          allow_org_move: options.allowOrgMove ?? false,
        }),
      },
    )
    return validateScopedOnboardingRun(run, targetIds)
  }

  /** Read-only render of the welcome email; claims no step. */
  async getOnboardingEmailPreview(
    runId: string,
    targetId: string,
  ): Promise<OnboardingEmailPreview> {
    return this.request(`/admin/onboarding/runs/${runId}/targets/${targetId}/email/preview`)
  }

  /** Mails the placeholder version to the signed-in operator; claims no step. */
  async sendOnboardingTestEmail(
    runId: string,
    targetId: string,
  ): Promise<OnboardingTestSendResult> {
    return this.request(`/admin/onboarding/runs/${runId}/targets/${targetId}/email/test-send`, {
      method: 'POST',
    })
  }

  /** Switch state; answers even when onboarding is turned off. */
  async getOnboardingCapabilities(): Promise<OnboardingCapabilities> {
    return this.request('/admin/onboarding/capabilities')
  }

  async getOnboardingEmailTemplate(runId: string): Promise<OnboardingEmailTemplate> {
    return this.request(`/admin/onboarding/runs/${runId}/email/template`)
  }

  /** Lint and render an unsaved draft for one recipient. Stores nothing. */
  async previewOnboardingEmailTemplate(
    runId: string,
    draft: OnboardingEmailTemplateDraft,
    targetId: string | null,
  ): Promise<OnboardingEmailTemplatePreview> {
    return this.request(`/admin/onboarding/runs/${runId}/email/template/preview`, {
      method: 'POST',
      body: JSON.stringify({ ...draft, target_id: targetId }),
    })
  }

  /** Select a custom template. Clears the test-send attestation server-side. */
  async saveOnboardingEmailTemplate(
    runId: string,
    draft: OnboardingEmailTemplateDraft,
  ): Promise<OnboardingEmailTemplate> {
    return this.request(`/admin/onboarding/runs/${runId}/email/template`, {
      method: 'PUT',
      body: JSON.stringify(draft),
    })
  }

  async resetOnboardingEmailTemplate(runId: string): Promise<OnboardingEmailTemplate> {
    return this.request(`/admin/onboarding/runs/${runId}/email/template`, { method: 'DELETE' })
  }

  async settleOnboardingStep(
    runId: string,
    targetId: string,
    step: OnboardingStepName,
    outcome: OnboardingSettleOutcome,
    targetIds?: string[],
  ): Promise<OnboardingRun> {
    const run = await this.request<OnboardingRun>(
      `/admin/onboarding/runs/${runId}/targets/${targetId}/steps/${step}/settle${onboardingScopeQuery(targetIds)}`,
      { method: 'POST', body: JSON.stringify({ outcome }) },
    )
    return validateScopedOnboardingRun(run, targetIds)
  }

  async cancelOnboardingRun(runId: string): Promise<OnboardingRun> {
    return this.request(`/admin/onboarding/runs/${runId}/cancel`, { method: 'POST' })
  }

  async listOnboardingOrgs(): Promise<OnboardingOrgSummary[]> {
    return this.request('/admin/onboarding/orgs')
  }

  async getOnboardingOrgRoster(slug: string): Promise<OnboardingOrgRoster> {
    return this.request(`/admin/onboarding/orgs/${encodeURIComponent(slug)}`)
  }

  /** The teams an existing account owns or administers, for the "join a team" picker. */
  async lookupOnboardingTeams(email: string): Promise<OnboardingTeamLookup> {
    return this.request(`/admin/onboarding/teams?email=${encodeURIComponent(email)}`)
  }

  async operatorTopup(
    targetEmail: string,
    sparks: number,
    reason?: string,
    targetTeamId?: string,
  ): Promise<{
    target_user_id: string
    target_email: string
    sparks: number
    balance_after: number
    already_processed: boolean
    created_at: string
    credited_team_id: string | null
    resolution_leg: string | null
  }> {
    const idempotencyKey = `topup_${Date.now()}_${Math.random().toString(36).slice(2)}`
    return this.request('/admin/sparks/topup', {
      method: 'POST',
      body: JSON.stringify({
        target_email: targetEmail,
        sparks: sparks,
        reason: reason || 'Operator top-up',
        idempotency_key: idempotencyKey,
        // Omitted rather than sent as null when blank: the field is optional on
        // the server and an explicit null would still read as "a team was
        // named".
        ...(targetTeamId ? { target_team_id: targetTeamId } : {}),
      }),
    })
  }

  // Operator-only: read a target user's remaining credits before acting on
  // them. Same topup_operator gate as the mint. Read-only on the server — it
  // reports `wallet_initialized: false` for a user with no balance row rather
  // than creating one.
  async operatorGetBalance(targetEmail: string): Promise<{
    target_user_id: string
    target_email: string
    balance: number
    wallet_initialized: boolean
  }> {
    return this.request(`/admin/sparks/balance?email=${encodeURIComponent(targetEmail)}`)
  }

  // Operator-only: which wallets can be credited for this user, and what each
  // currently holds. Backs the team picker on the top-up page, so a team id is
  // chosen rather than transcribed.
  //
  // `balance: null` is NOT zero — it means the team has never been funded,
  // which is a different sentence in front of an operator than "spent down to
  // nothing". `teams_enabled: false` means teams is off FOR THE RECIPIENT and
  // every team id would be refused by the mint.
  async operatorGetUserTeams(targetEmail: string): Promise<{
    target_user_id: string
    target_email: string
    teams_enabled: boolean
    teams: Array<{
      team_id: string
      name: string
      kind: string
      role: string
      balance: number | null
      balance_updated_at: string | null
    }>
  }> {
    return this.request(`/admin/sparks/user-teams?email=${encodeURIComponent(targetEmail)}`)
  }

  // Operator-only: remove credits FROM a user by email — the inverse of
  // operatorTopup, and gated on the same permission.
  //
  // `sparks` is always POSITIVE; the direction is the endpoint, not the sign.
  // Same per-call idempotency key rationale as operatorTopup: an internal
  // network retry of THIS submit must not double-deduct, while a fresh click
  // is a new intended deduction.
  async operatorDeduct(
    targetEmail: string,
    sparks: number,
    reason?: string,
  ): Promise<{
    target_user_id: string
    target_email: string
    sparks: number
    balance_after: number
    already_processed: boolean
    created_at: string
  }> {
    const idempotencyKey = `deduct_${Date.now()}_${Math.random().toString(36).slice(2)}`
    return this.request('/admin/sparks/deduct', {
      method: 'POST',
      body: JSON.stringify({
        target_email: targetEmail,
        sparks: sparks,
        reason: reason || 'Operator deduction',
        idempotency_key: idempotencyKey,
      }),
    })
  }

  // RFC-1612: request a C2PA-signed copy of a stored MP4 for download. Returns
  // the stored signed copy at once, or `pending` while the video worker signs
  // it (poll `getC2paDownloadStatus`). retryTransient:false: a retried request
  // could re-queue a failed signing job.
  async prepareC2paDownload(projectId: string, assetPath: string): Promise<C2paDownloadResult> {
    const payload: ApiValue = await this.request<ApiValue>(
      `/projects/${encodeURIComponent(projectId)}/assets/c2pa-download`,
      {
        method: 'POST',
        body: JSON.stringify({ asset_path: assetPath }),
        retryTransient: false,
      },
    )
    return decodeC2paDownload(payload)
  }

  // RFC-1612: status of a requested signed copy. Read-only, never queues work.
  async getC2paDownloadStatus(projectId: string, assetPath: string): Promise<C2paDownloadResult> {
    const query = new URLSearchParams({ asset_path: assetPath })
    const payload: ApiValue = await this.request<ApiValue>(
      `/projects/${encodeURIComponent(projectId)}/assets/c2pa-download?${query.toString()}`,
    )
    return decodeC2paDownload(payload)
  }

  // ---- RFC-0856 I7: feature-flag admin (/admin/flags) --------------------
  // Admin surface, gated by AdminFlagsRoute + the backend broad-admin/scoped
  // flags permission (require_flags_admin). The 403/404/409/503 are the real boundary.
  async listFlags(): Promise<FlagListItem[]> {
    return this.request('/admin/flags')
  }

  async getFlag(key: string): Promise<FlagListItem> {
    return this.request(`/admin/flags/${encodeURIComponent(key)}`)
  }

  // Set a flag value with optimistic concurrency. `expectedVersion` is the
  // version the operator last read (0 when the flag has never been set); the
  // backend returns 409 FLAG_VERSION_CONFLICT if it moved underneath us.
  // `confirm` opts past the sensitive-flag guard (409 FLAG_CONFIRM_REQUIRED).
  // retryTransient:false so a 503 FLAG_LOCK_TIMEOUT surfaces immediately (a
  // committed-but-lost write must NOT be auto-retried into a misleading 409).
  async setFlag(
    key: string,
    value: ApiValue,
    expectedVersion: number,
    confirm?: boolean,
  ): Promise<FlagListItem> {
    // ApiValue includes undefined; JSON.stringify would silently drop the
    // `value` key, sending a bodyless write. Fail loudly instead.
    if (value === undefined) {
      throw new Error('Flag value must be defined and JSON-serializable')
    }
    return this.request(`/admin/flags/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({
        value,
        expected_version: expectedVersion,
        ...(confirm ? { confirm: true } : {}),
      }),
      retryTransient: false,
    })
  }

  // Recovery lever: re-publish a fleet cache invalidate-all. No DB write.
  async refreshFlags(): Promise<{ ok: boolean }> {
    return this.request('/admin/flags/refresh', {
      method: 'POST',
      retryTransient: false,
    })
  }

  async listInternalModelEntitlements(): Promise<InternalModelEntitlementListResponse> {
    const raw = await this.request<ApiValue>('/admin/internal-model-entitlements')
    return decodeInternalModelEntitlementListResponse(raw)
  }

  async grantInternalModelEntitlement(email: string): Promise<InternalModelEntitlementItem> {
    const raw = await this.request<ApiValue>('/admin/internal-model-entitlements', {
      method: 'POST',
      body: JSON.stringify({ email }),
      retryTransient: false,
    })
    return decodeInternalModelEntitlementItem(raw)
  }

  async revokeInternalModelEntitlement(userId: string): Promise<InternalModelEntitlementItem> {
    const raw = await this.request<ApiValue>(`/admin/internal-model-entitlements/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      retryTransient: false,
    })
    return decodeInternalModelEntitlementItem(raw)
  }

  async getPricing(): Promise<{ pricing: PricingItem[] }> {
    return this.request('/pricing')
  }

  // User feedback (DB-first; the backend mirrors to a GitHub issue best-effort).
  async submitWorkspaceFeedback(
    projectId: string,
    request: DebugFeedbackRequest
  ): Promise<DebugFeedbackResponse> {
    return this.request(`/projects/${projectId}/workspace/feedback`, {
      method: 'POST',
      body: JSON.stringify(request),
      // Non-idempotent write: a transient 5xx auto-retry could double-insert a
      // feedback row. The GitHub mirror runs in a background task server-side,
      // so this POST only waits on the DB commit and stays fast.
      retryTransient: false,
    })
  }

  // GitHub Feedback API (creates GitHub Issues from user feedback)
  async submitGithubFeedback(
    request: GithubFeedbackRequest
  ): Promise<GithubFeedbackResponse> {
    return this.request('/api/feedback/github-issue', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  /**
   * Cancel the running agent execution for a project.
   * RFC 0061: Stop button for canvas execution.
   */
  async cancelAgentExecution(projectId: string, canvasId?: string): Promise<{ cancelled: boolean; reset_nodes?: number; reason?: string }> {
    const query = canvasId ? `?canvas_id=${encodeURIComponent(canvasId)}` : ''
    return this.request(`/projects/${projectId}/agentic/cancel-node${query}`, {
      method: 'POST',
    })
  }

  async getCostSummary(projectId: string): Promise<CostSummaryResponse> {
    return this.request(`/projects/${projectId}/agentic/cost-summary`)
  }

  // Dashboard Tabs
  //
  // Every call carries the tab SCOPE: which team, and whether these are the
  // caller's private tabs inside it or the set the whole team shares. Omitting
  // `team_id` is the pre-teams request and the server answers with the single
  // per-user tab set, so a client with teams disabled needs no changes.
  async listTabs(scope?: TabScope): Promise<{ tabs: DashboardTab[] }> {
    return this.request(`/dashboard/tabs${tabScopeQuery(scope)}`)
  }

  async createTab(name: string, scope?: TabScope): Promise<DashboardTab> {
    return this.request(`/dashboard/tabs${tabScopeQuery(scope)}`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  async updateTab(
    tabId: string,
    data: { name?: string; sort_by?: TabSortBy; sort_direction?: TabSortDirection },
    scope?: TabScope
  ): Promise<DashboardTab> {
    return this.request(`/dashboard/tabs/${tabId}${tabScopeQuery(scope)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteTab(tabId: string, scope?: TabScope): Promise<void> {
    return this.request(`/dashboard/tabs/${tabId}${tabScopeQuery(scope)}`, {
      method: 'DELETE',
    })
  }

  async reorderTabs(tabIds: string[], scope?: TabScope): Promise<{ tabs: DashboardTab[] }> {
    return this.request(`/dashboard/tabs/reorder${tabScopeQuery(scope)}`, {
      method: 'PUT',
      body: JSON.stringify({ tab_ids: tabIds }),
    })
  }

  async assignProjectToTab(tabId: string, projectId: string, scope?: TabScope): Promise<void> {
    return this.request(
      `/dashboard/tabs/${tabId}/projects/${projectId}${tabScopeQuery(scope)}`,
      { method: 'POST' }
    )
  }

  async unassignProjectFromTab(tabId: string, projectId: string, scope?: TabScope): Promise<void> {
    return this.request(
      `/dashboard/tabs/${tabId}/projects/${projectId}${tabScopeQuery(scope)}`,
      { method: 'DELETE' }
    )
  }

  async reorderProjects(
    tabId: string,
    projectIds: string[],
    offset = 0,
    scope?: TabScope
  ): Promise<void> {
    return this.request(`/dashboard/tabs/${tabId}/projects/reorder${tabScopeQuery(scope)}`, {
      method: 'PUT',
      body: JSON.stringify({ project_ids: projectIds, offset }),
    })
  }

  async listTabProjects(
    tabId: string,
    opts: {
      limit?: number
      offset?: number
      sortBy?: TabSortBy
      sortDirection?: TabSortDirection
      /** Keep dashboard pins ahead of the selected sort. Defaults to true. */
      starredFirst?: boolean
      /** Include the workflow-derived action used by signed-in landing cards. */
      includeResumeAction?: boolean
      /**
       * Case-insensitive SUBSTRING (contains) match against the project name.
       * Empty / whitespace-only strings are a no-op (backend already strips).
       * Omit the param entirely when not searching so the URL stays clean and
       * the backend skips the filter clause. Named `namePrefix` for continuity
       * with the retained `name_prefix` wire param (historical name).
       */
      namePrefix?: string
      /** RFC-1371: scope the list to one team. Omitted = everything you own. */
      teamId?: string
      /**
       * RFC-1371 D3: which half to show — 'private' or 'team'. Applies to
       * EVERY tab, so search inherits the same scope. Ignored server-side
       * unless `teamId` is also sent.
       */
      visibility?: string
    } = {},
  ): Promise<PaginatedResponse<ProjectListItem>> {
    const {
      limit = 10,
      offset = 0,
      sortBy,
      sortDirection,
      starredFirst,
      includeResumeAction,
      namePrefix,
      teamId,
      visibility,
    } = opts
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    // Named tabs ignore sort query params server-side; pass them only for "all"
    // to avoid noisy URLs.
    if (sortBy && sortBy !== 'manual') qs.set('sort_by', sortBy)
    if (sortDirection) qs.set('sort_direction', sortDirection)
    if (teamId) qs.set('team_id', teamId)
    // Only alongside a team: unscoped the backend ignores it, and sending it
    // anyway would imply the pre-teams list is being narrowed when it is not.
    if (teamId && visibility) qs.set('visibility', visibility)
    if (starredFirst !== undefined) qs.set('starred_first', String(starredFirst))
    if (includeResumeAction !== undefined) {
      qs.set('include_resume_action', String(includeResumeAction))
    }
    if (namePrefix && namePrefix.trim()) qs.set('name_prefix', namePrefix.trim())
    return this.request(
      `/dashboard/tabs/${encodeURIComponent(tabId)}/projects?${qs.toString()}`,
    )
  }
}

export interface NodeUsageItem {
  node_id: string
  node_type: string
  action: string
  cost_category: string
  node_label: string
  input_tokens: number
  output_tokens: number
  llm_model: string
  token_cost_usd: number
  images_generated: number
  videos_generated: number
  audio_generated: number
  video_seconds: number
  estimated_sparks: number
  charged_sparks: number
  duration_ms: number | null
  started_at: string | null
  status: 'COMPLETED' | 'FAILED'
}

export interface CostSummaryResponse {
  total_sparks_charged: number
  total_audio_sparks_charged: number
  total_sparks_estimated: number
  total_input_tokens: number
  total_output_tokens: number
  total_token_cost_usd: number
  total_images: number
  total_videos: number
  total_audio: number
  node_count: number
  nodes: NodeUsageItem[]
}

// User feedback API types (DB-first path; the backend mirrors to GitHub).
export interface DebugFeedbackRequest {
  feedback: string
  /** Short summary — used as the GitHub issue title + prefixed onto the stored text. */
  title?: string
  category?: 'bug' | 'feature' | 'question' | 'other'
  browser_info?: {
    user_agent?: string
    url?: string
  }
}

export interface DebugFeedbackResponse {
  id: string
  timestamp: string
}

// GitHub Feedback API types
export interface GithubFeedbackRequest {
  title: string
  description: string
  category: 'bug' | 'feature_request' | 'question'
  project_id?: string
  browser_info?: string
  page_url?: string
}

export interface GithubFeedbackResponse {
  issue_number: number
  html_url: string
}

// =============================================================================
// Gateway Config API
// =============================================================================

// Capabilities JSONB: most keys are string arrays (duration_range, aspect_ratios, etc.)
// but request_pattern is a plain string.  Using a union keeps the type honest.
type CapabilitiesValue = string[] | string
export type GatewayConfigKind = 'video' | 'director' | 'image'
export type DirectorByomProvider = 'anthropic_messages' | 'openai_chat_completions'
export type VideoByomPresetId = 'custom' | 'byteplus_seedance_2'
export type { VideoByomProtocolConfigV1 as VideoByomProtocolConfig } from '../generated/videoByomProtocol'

export interface VideoByomPreset {
  id: VideoByomPresetId
  label: string
  description: string
  run_url: string | null
  provider_model: string | null
  protocol: string
  capabilities: Record<string, string[]>
  input_mode: string
}

export interface GatewayConfig {
  id: string
  api_key_masked: string   // masked: "sk-...xxxx"
  base_url: string | null
  enabled: boolean
  key_name: string | null
  capabilities: Record<string, CapabilitiesValue> | null
  is_default: boolean
  model_kind: GatewayConfigKind
  director_provider: DirectorByomProvider | null
  provider_model: string | null
  video_preset_id?: VideoByomPresetId | null
  protocol_config?: VideoByomProtocolConfigV1 | null
  needs_api_key?: boolean
  created_at: string
  updated_at: string
}

export interface CreateGatewayConfigRequest {
  api_key?: string
  base_url?: string
  key_name?: string
  capabilities?: Record<string, CapabilitiesValue>
  is_default?: boolean
  model_kind?: GatewayConfigKind
  director_provider?: DirectorByomProvider
  provider_model?: string
  video_preset_id?: VideoByomPresetId
  protocol_config?: VideoByomProtocolConfigV1
}

export interface UpdateGatewayConfigRequest {
  api_key?: string
  base_url?: string
  enabled?: boolean
  key_name?: string
  capabilities?: Record<string, CapabilitiesValue>
  is_default?: boolean
  model_kind?: GatewayConfigKind
  director_provider?: DirectorByomProvider
  provider_model?: string
  video_preset_id?: VideoByomPresetId
  protocol_config?: VideoByomProtocolConfigV1
}

export interface ByomModelInfo {
  id: string
  key_name: string | null
  capabilities: Record<string, CapabilitiesValue> | null
  is_default: boolean
  enabled: boolean
}

export interface VideoModelsBySourceResponse {
  native: string[]
  gateway: string[]
  byom: ByomModelInfo[]
  default_model: string | null
  /**
   * Offered models that support Closing frame (`LAST_FRAME`, RFC-1668). Absent
   * from an API that predates it, which hides the frame control.
   */
  last_frame_models?: string[]
}

/** A profile choice is copied into new projects only, never read at runtime. */
export interface ProfileVideoDefaultResponse {
  stored_id: string | null
  seed_id: string | null
  status: 'ready' | 'invalid' | 'unavailable' | 'load_failed'
}

export async function getProfileVideoDefault(): Promise<ProfileVideoDefaultResponse> {
  return apiRequest<ProfileVideoDefaultResponse>('/user/default-video-model')
}

export async function setProfileVideoDefault(modelId: string | null): Promise<ProfileVideoDefaultResponse> {
  return apiRequest<ProfileVideoDefaultResponse>('/user/default-video-model', {
    method: 'PUT',
    body: JSON.stringify({ model_id: modelId }),
  })
}

export interface ImageModelsResponse {
  native: string[]
  byom: ByomModelInfo[]
  default_model: string | null
}

export async function getGatewayConfigs(): Promise<GatewayConfig[]> {
  const resp = await apiRequest<{ configs: GatewayConfig[] }>('/gateway-configs')
  return resp.configs
}

export async function getVideoByomPresets(): Promise<VideoByomPreset[]> {
  const resp = await apiRequest<{ presets: VideoByomPreset[] }>('/gateway-configs/video-presets')
  return resp.presets
}

export async function createGatewayConfig(config: CreateGatewayConfigRequest): Promise<GatewayConfig> {
  return apiRequest<GatewayConfig>('/gateway-configs', {
    method: 'POST',
    body: JSON.stringify(config),
  })
}

export async function updateGatewayConfig(id: string, updates: UpdateGatewayConfigRequest): Promise<GatewayConfig> {
  return apiRequest<GatewayConfig>(`/gateway-configs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

export async function deleteGatewayConfig(id: string): Promise<void> {
  await apiRequest<void>(`/gateway-configs/${id}`, { method: 'DELETE' })
}

export interface TestConnectionResult {
  status: 'reachable' | 'auth_failed' | 'unreachable'
  detail?: string
  latency_ms?: number
}

export async function testGatewayConnection(url: string): Promise<TestConnectionResult> {
  return apiRequest<TestConnectionResult>('/gateway-configs/test-connection', {
    method: 'POST',
    body: JSON.stringify({ url }),
  })
}

export interface TestCredentialsRequest {
  url: string
  api_key?: string
  config_id?: string
  trace_id?: string
  model_kind?: GatewayConfigKind
  director_provider?: DirectorByomProvider
  provider_model?: string
  video_preset_id?: VideoByomPresetId
}

export interface TestCredentialsResult {
  status: 'auth_success' | 'auth_failed' | 'unsupported' | 'unavailable' | 'provider_error' | 'model_unavailable'
  supported: boolean
  provider?: string | null
  detail?: string | null
  provider_status?: number | null
  latency_ms?: number | null
  key_source: 'typed' | 'saved' | 'none'
}

export async function testGatewayCredentials(
  request: TestCredentialsRequest,
): Promise<TestCredentialsResult> {
  return apiRequest<TestCredentialsResult>('/gateway-configs/test-credentials', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function resolveVideoFamilyForNode(data: {
  family_id: string; mode: string; image_count: number; video_count: number; audio_count: number
}): Promise<{ model_id: string; mode: string }> {
  const result = await apiRequest<{ model_id: string | null; mode: string | null; incompatibility: string | null }>(
    '/video-models/resolve-family', { method: 'POST', body: JSON.stringify(data) },
  )
  if (!result.model_id || !result.mode) throw new Error(result.incompatibility ?? 'Video family could not be resolved')
  return { model_id: result.model_id, mode: result.mode }
}

export async function getVideoModels(): Promise<VideoModelsBySourceResponse> {
  return apiRequest<VideoModelsBySourceResponse>('/video-models')
}

export async function getImageModels(): Promise<ImageModelsResponse> {
  return apiRequest<ImageModelsResponse>('/image-models')
}

export async function getDirectorModels(): Promise<DirectorModelsResponse> {
  return apiRequest<DirectorModelsResponse>('/director-models')
}

export async function setDirectorModelPreference(
  model: DirectorModel,
): Promise<DirectorModelSelectionResponse> {
  const response = await apiRequest<DirectorModelSelectionResponse>('/director-models/selection', {
    method: 'PUT',
    body: JSON.stringify({ model_id: model }),
  })
  setDirectorModel(response.selected_model)
  return response
}

export const api = new ApiClient()

// Workspace conversation message shape (kept for ConversationWithMessages typing).
// The SSE chat endpoint and its `sendWorkspaceChat` client were removed as part
// of the DREAMWRIGHT_VIP promo-code remediation (T277312397) — those routes were
// the only consumers of the operator GOOGLE_API_KEY fallback. The Director chat
// uses `/agentic/agent/connect|respond` instead.
export interface WorkspaceChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

// =============================================================================
// Voice listing
// =============================================================================

export interface VoiceSummary {
  provider: string
  provider_label: string
  voice_id: string
  name: string
  description: string
  preview_url: string | null
  labels: Record<string, string>
}

export interface CharacterVoiceCatalogueEntry {
  provider: string
  provider_label: string
  voice_source: 'elevenlabs_voice_library'
  voice_ref: string
  voice_id: string
  name: string
  description: string
  preview_url: string | null
  language: string | null
  locale: string | null
  use_case: string | null
  descriptors: string[]
}

export type ElevenLabsVoiceCatalogueEntry = CharacterVoiceCatalogueEntry

export interface CharacterVoiceCatalogueSearchResult {
  voices: CharacterVoiceCatalogueEntry[]
  page: number
  page_size: number
  has_more: boolean
  next_page: number | null
  total_count: number | null
}

export type ElevenLabsVoiceCatalogueSearchResult =
  CharacterVoiceCatalogueSearchResult

export interface CharacterVoiceCatalogueSearchParams {
  query?: string
  language?: string
  gender?: string
  age?: string
  accent?: string
  use_case?: string
  page?: number
  page_size?: number
}

export type ElevenLabsVoiceCatalogueSearchParams =
  CharacterVoiceCatalogueSearchParams

export async function searchCharacterVoiceCatalogue(
  params: CharacterVoiceCatalogueSearchParams = {},
): Promise<CharacterVoiceCatalogueSearchResult> {
  const searchParams = new URLSearchParams()
  const query = params.query?.trim()
  if (query) searchParams.set('query', query)
  const language = params.language?.trim()
  if (language) searchParams.set('language', language)
  const gender = params.gender?.trim()
  if (gender) searchParams.set('gender', gender)
  const age = params.age?.trim()
  if (age) searchParams.set('age', age)
  const accent = params.accent?.trim()
  if (accent) searchParams.set('accent', accent)
  const useCase = params.use_case?.trim()
  if (useCase) searchParams.set('use_case', useCase)
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.page_size != null) searchParams.set('page_size', String(params.page_size))
  const suffix = searchParams.toString()
  return apiRequest<CharacterVoiceCatalogueSearchResult>(
    suffix ? `/voices/elevenlabs/catalogue?${suffix}` : '/voices/elevenlabs/catalogue',
  )
}

export const searchElevenLabsVoiceCatalogue = searchCharacterVoiceCatalogue

export async function validateCharacterVoiceCatalogueSelection(
  voiceRef: string,
): Promise<VoiceSummary> {
  return apiRequest<VoiceSummary>('/voices/elevenlabs/catalogue/validate', {
    method: 'POST',
    body: JSON.stringify({ voice_ref: voiceRef }),
  })
}

export type VoiceCatalogueSelectionTargetType =
  | 'character'
  | 'sound_expert'
  | 'script_voice_role'
  | 'video_editor_change_voice'

export type ApplyVoiceCatalogueSelectionTargetType =
  | 'character'
  | 'sound_expert'
  | 'script_voice_role'

export interface ApplyVoiceCatalogueSelectionResponse {
  success: boolean
  target_type: ApplyVoiceCatalogueSelectionTargetType
  applied_node_id: string
  selected_voice_name: string
  selected_voice_ref: string
  rerun_scheduled: boolean
  warnings: string[]
  message: string
}

export async function applyVoiceCatalogueSelection(
  projectId: string,
  canvasId: string,
  selectionContext: {
    type: ApplyVoiceCatalogueSelectionTargetType
    node_id: string
    voice_role_id?: string | null
  },
  voiceRef: string,
  sampleText?: string | null,
): Promise<ApplyVoiceCatalogueSelectionResponse> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  return apiRequest<ApplyVoiceCatalogueSelectionResponse>(
    `/projects/${projectId}/agentic/voice-catalogue/apply?${params.toString()}`,
    {
      method: 'POST',
      retryTransient: false,
      body: JSON.stringify({
        voice_ref: voiceRef,
        selection_context: selectionContext,
        sample_text: sampleText ?? undefined,
      }),
    },
  )
}

export async function clearVoiceCatalogueSelection(
  projectId: string,
  canvasId: string,
  nodeId: string,
): Promise<{ success: boolean; applied_node_id: string; message: string }> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  return apiRequest(`/projects/${projectId}/agentic/voice-catalogue/clear?${params.toString()}`, {
    method: 'POST',
    retryTransient: false,
    body: JSON.stringify({ node_id: nodeId }),
  })
}

// `sendWorkspaceChat` (POST /projects/{id}/workspaces/{wid}/messages) and
// `executeWorkspaceTool` (POST .../{wid}/execute-tool) were removed as part
// of the DREAMWRIGHT_VIP promo-code remediation (T277312397). They were the
// only frontend callers of the operator GOOGLE_API_KEY fallback. The active
// Director chat path is `/agentic/agent/connect|respond`; tool execution
// goes through `/projects/{id}/agentic/execute-node`.
/**
 * TEST-ONLY: reset this module's process-level globals between tests.
 *
 * Under Vitest `isolate: false` the module registry is shared per worker, so
 * these module-level singletons would otherwise leak state from one test file
 * into the next. Called from `src/test-utils/setup.ts` before AND after each
 * test. Do NOT call from production code.
 *
 * Covers auth state (token getter, cached token, unauthorized handler) and the
 * publication token. `localStorage`-backed values (promo
 * code, API base, provider keys, video model) are intentionally NOT reset here
 * — `setup.ts` already clears storage in its afterEach, which covers them.
 */
export function __resetApiClientStateForTests(): void {
  authTokenGetter = null
  cachedAuthToken = null
  unauthorizedHandler = null
  currentPublicationToken = null
}


// ============================================================================
// Conversation API
// ============================================================================

export interface WorkflowState {
  stage: string  // 'idle', 'expand_story', 'generate_characters', etc.
  active_job_id?: string | null  // Job currently running
  pending_action?: string | null  // 'continue' | 'refine'
}

export interface Conversation {
  id: string
  project_id: string
  title: string
  active_canvas_id: string  // Which canvas this conversation is working on
  workflow?: WorkflowState | null  // Workflow progress tracking
  created_at: string
  updated_at: string
}

export interface ConversationWithMessages extends Conversation {
  messages: WorkspaceChatMessage[]
  // Note: canvas_state is no longer part of conversation - canvases are independent
}

export interface CanvasState {
  nodes: object[]
  edges: object[]
  viewport?: { x: number; y: number; zoom: number } | null
  tainted_nodes?: string[]
  updated_at?: string
}

export interface YDocNodePresence {
  present_node_ids: string[]
  missing_node_ids: string[]
}

export interface YDocRoomState {
  project_id: string
  canvas_id: string
  room_name: string
  room_epoch: number
  force_db_load: boolean
  source: string
  source_snapshot_id?: string | null
  reason?: string | null
  requested_by?: string | null
  updated_at?: string | null
}

// Conversation API helper — kept as a name so the public surface (listConversations etc.)
// reads cleanly. Delegates to apiRequest, which already handles empty bodies.
function conversationRequest<T>(path: string, options?: ApiFetchOptions): Promise<T> {
  return apiRequest<T>(path, options)
}

/**
 * List all conversations for a project
 */
export async function listConversations(projectId: string): Promise<Conversation[]> {
  const response = await conversationRequest<{ conversations: Conversation[] }>(
    `/projects/${projectId}/workspaces`
  )
  return response.conversations || []
}

/**
 * Create a new conversation
 */
export async function createConversation(
  projectId: string,
  options?: { title?: string; canvas_id?: string }
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  })
}

/**
 * Get or create the agent conversation (RFC 0036)
 */
export async function getOrCreateAgentConversation(
  projectId: string
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces/agent`, {
    method: 'POST',
  })
}

/**
 * Update a conversation (e.g., rename or switch canvas)
 */
export async function updateConversation(
  projectId: string,
  conversationId: string,
  options: { title?: string; canvas_id?: string; workflow?: WorkflowState }
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify(options),
  })
}

/**
 * Get a conversation with messages
 */
export async function getConversation(
  projectId: string,
  conversationId: string
): Promise<ConversationWithMessages> {
  return conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`)
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  projectId: string,
  conversationId: string
): Promise<void> {
  await conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`, {
    method: 'DELETE',
  })
}

/**
 * List all canvases for a project
 */
export type CanvasAgentType = 'director' | 'dreamvibe'

export type CanvasSummary = {
  id: string
  agent_type: CanvasAgentType
  agent_id: string
  director_id: string
  title: string
  updated_at: string
  created_at?: string
  node_count: number
  initial_prompt?: string | null
  source_node_ids?: string[]
  copied_nodes?: ApiRecord[]
  copied_edges?: ApiRecord[]
  skipped_edges?: ApiRecord[]
  dropped_edge_inputs?: ApiRecord[]
  id_map?: Record<string, string>
}

export type CreateCanvasRequest = {
  canvas_id?: string
  title?: string
  prompt?: string
  initial_prompt?: string
  agent_type?: CanvasAgentType
  source_node_ids?: string[]
  source_canvas_id?: string
  include_internal_edges?: boolean
  preserve_positions?: boolean
}

export type CanvasListResponse = {
  canvases: CanvasSummary[]
  multi_canvas_enabled: boolean
}

export async function getCanvasChangeHints(
  projectId: string,
  canvasId: string,
  nodeId: string,
  clientState: string,
  clientDeletes: string,
): Promise<CanvasChangeHints> {
  const response = await apiRequest<ApiValue>(
    '/projects/' + encodeURIComponent(projectId) + '/agentic/canvas-observed-by?canvas_id=' + encodeURIComponent(canvasId) + '&node_id=' + encodeURIComponent(nodeId) + '&client_state=' + encodeURIComponent(clientState) + '&client_deletes=' + encodeURIComponent(clientDeletes),
  )
  return decodeCanvasChangeHints(response)
}

export async function listCanvases(
  projectId: string
): Promise<CanvasListResponse> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases`)
}

/**
 * Create a canvas tab.
 */
export async function createCanvas(
  projectId: string,
  request: CreateCanvasRequest = {},
): Promise<CanvasSummary> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

/**
 * Rename a canvas tab.
 */
export async function renameCanvas(
  projectId: string,
  canvasId: string,
  title: string,
): Promise<CanvasSummary> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  })
}

/**
 * Get canvas state by ID (canvases are independent from conversations)
 */
export async function getCanvasState(
  projectId: string,
  canvasId: string = 'agent'
): Promise<CanvasState> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`)
}

/**
 * Read live backend YDoc node presence after frontend sync writes.
 */
export async function getYDocNodePresence(
  projectId: string,
  canvasId: string,
  nodeIds: string[],
  options?: ApiFetchOptions,
): Promise<YDocNodePresence> {
  const params = new URLSearchParams()
  for (const nodeId of nodeIds) {
    params.append('node_ids', nodeId)
  }
  const query = params.toString()
  return conversationRequest(
    `/projects/${projectId}/workspaces/canvases/${canvasId}/ydoc-node-presence${query ? `?${query}` : ''}`,
    options,
  )
}

/**
 * Read room-control metadata before creating a Yjs WebSocketProvider.
 */
export async function getYDocRoomState(
  projectId: string,
  canvasId: string,
): Promise<YDocRoomState> {
  return conversationRequest(
    `/projects/${projectId}/workspaces/canvases/${canvasId}/room-state`,
  )
}

export async function getVideoEditorAudioActions(
  projectId: string,
  nodeId: string,
  canvasId: string,
): Promise<VideoEditorAudioActionsResponse> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  const raw = await apiRequest<ApiValue>(
    `/projects/${projectId}/agentic/nodes/${nodeId}/video-editor-audio-actions?${params.toString()}`,
  )
  return decodeVideoEditorAudioActionsResponse(raw)
}

export async function runVideoEditorAudioAction(
  projectId: string,
  nodeId: string,
  canvasId: string,
  actionId: VideoEditorAudioActionId,
  targetId: string,
  options: {
    splitJobId?: string
    timelineConfig?: TimelineConfigDict
    voiceRef?: string
    voiceId?: string
  } = {},
): Promise<VideoEditorAudioActionRunResponse> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  const raw = await apiRequest<ApiValue>(
    `/projects/${projectId}/agentic/nodes/${nodeId}/video-editor-audio-actions/run?${params.toString()}`,
    {
      method: 'POST',
      retryTransient: false,
      body: JSON.stringify({
        action_id: actionId,
        target_id: targetId,
        split_job_id: options.splitJobId,
        timeline_config: options.timelineConfig,
        voice_ref: options.voiceRef,
        voice_id: options.voiceId,
      }),
    },
  )
  return decodeVideoEditorAudioActionRunResponse(raw)
}

export interface SoundExpertAudioTrimResponse {
  out_audio: SoundGeneratedAudioDict
  out_duration_seconds: number
  picked_index: number
  local_path: string
}

export async function trimSoundExpertAudio(
  projectId: string,
  nodeId: string,
  canvasId: string,
  request: {
    historyIndex: number
    expectedSourcePath: string
    startS: number
    endS: number
  },
): Promise<SoundExpertAudioTrimResponse> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  return apiRequest<SoundExpertAudioTrimResponse>(
    `/projects/${projectId}/agentic/nodes/${nodeId}/sound-expert-audio/trim?${params.toString()}`,
    {
      method: 'POST',
      retryTransient: false,
      body: JSON.stringify({
        history_index: request.historyIndex,
        expected_source_path: request.expectedSourcePath,
        start_s: request.startS,
        end_s: request.endS,
      }),
    },
  )
}

export async function selectSoundExpertAudioVariant(
  projectId: string,
  nodeId: string,
  canvasId: string,
  historyIndex: number,
  expectedSourcePath: string,
): Promise<{ success: boolean; picked_index: number }> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  return apiRequest(
    `/projects/${projectId}/agentic/nodes/${nodeId}/sound-expert-audio/select?${params.toString()}`,
    {
      method: 'POST',
      retryTransient: false,
      body: JSON.stringify({
        history_index: historyIndex,
        expected_source_path: expectedSourcePath,
      }),
    },
  )
}

/**
 * Delete a canvas (cannot delete the agent canvas)
 */
export async function deleteCanvas(
  projectId: string,
  canvasId: string
): Promise<void> {
  await conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`, {
    method: 'DELETE',
  })
}


// =============================================================================
// DAG Proposal API (RFC 0079: Manual Canvas Node Persistence)
// =============================================================================

/**
 * Execute a node through the Orchestrator (RFC-0098).
 *
 * Fire-and-forget: progress and completion arrive via the existing
 * /agent/connect SSE stream (node status changes propagate through YDoc).
 *
 * Node data (in_/out_ fields) must be written to YDoc before calling.
 * This endpoint only accepts commands: which node, what action, how many times.
 *
 * @param projectId - Project ID
 * @param nodeId - Node ID to execute
 * @param options - Execution options. `canvasId` is REQUIRED (see below).
 */
export interface ExecuteNodeOptions {
  /** Action to perform (default "generate") */
  action?: string
  /** Number of sequential generations to run (1-6, default 1) */
  count?: number
  /** Optional in_* fields to persist to YDoc before execution */
  inputUpdates?: ApiRecord
  /** Optional declared "state" fields to persist to YDoc before execution */
  stateUpdates?: ApiRecord
  /**
   * Active canvas id — REQUIRED. The backend scopes the node lookup to this
   * canvas's YDoc room; omitting it makes the backend fall back to the default
   * "agent" canvas, so any node on a secondary canvas 404s as "Node not found".
   * Callers inside the workspace should pass `useActiveCanvasId()`.
   */
  requestId?: string
  canvasId: string
  /** Keep the small command POST alive across a page refresh/navigation. */
  keepalive?: boolean
  /** See ApiFetchOptions.onRequestStart. */
  onRequestStart?: () => void
  /**
   * Context for the `node_generation_submitted` analytics event (node
   * type, panel tab, generation mode, reference count, model source). Never
   * sent to the backend.
   */
  analytics?: ExecuteNodeAnalytics
}

export async function executeNode(
  projectId: string,
  nodeId: string,
  options: ExecuteNodeOptions,
): Promise<{ status: string; node_id: string }> {
  const {
    action,
    count,
    inputUpdates,
    stateUpdates,
    canvasId,
    keepalive,
    onRequestStart,
  } = options
  const requestId = options.requestId ?? crypto.randomUUID()
  const body: ApiRecord = { node_id: nodeId, action: action || 'generate',
    request_id: requestId }
  if (count !== undefined && count > 1) {
    body.count = count
  }
  if (inputUpdates && Object.keys(inputUpdates).length > 0) {
    body.input_updates = inputUpdates
  }
  if (stateUpdates && Object.keys(stateUpdates).length > 0) {
    body.state_updates = stateUpdates
  }
  // Always send the canvas id so the backend scopes the node lookup correctly.
  body.canvas_id = canvasId

  canvasExecuteLog({
    event: 'execute-node-request',
    node_id: nodeId,
    action: action || 'generate',
    canvas_id: canvasId,
  })

  const response = await apiFetch(
    `/projects/${projectId}/agentic/execute-node`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      retryTransient: 'admission-only',
      keepalive,
      // RFC-1577: log the submit at the last moment before the request leaves,
      // after the caller's own hook (which may still cancel the dispatch).
      onRequestStart: () => {
        onRequestStart?.()
        trackNodeGenerationSubmitted({
          requestId,
          projectId,
          nodeId,
          action: action || 'generate',
          count: count ?? 1,
          stateUpdates,
          analytics: options.analytics,
        })
      },
    },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    // Backend returns 402 with structured detail when the User-path
    // sparks-balance gate trips. Surface as a typed error so callers
    // can render the InsufficientSparksModal instead of a toast.
    if (response.status === 402 && _isInsufficientSparksDetail(errorData?.detail)) {
      throw new InsufficientSparksError(
        errorData.detail.message ?? 'Insufficient credits',
      )
    }
    // CLAUDE.md #7: surface the provider's/backend's SPECIFIC reason verbatim. A
    // structured `detail` (object with status/code/finishReason/blockReason/message)
    // would coerce to "[object Object]" via Error's stringification, so serialize it
    // instead of losing it — callers assign this straight into the node error banner.
    // Typed with the HTTP status so a caller can tell a definitive refusal from
    // a transport failure whose outcome is unknown.
    const detail = errorData?.detail
    throw new ApiRequestError(
      typeof detail === 'string' && detail
        ? detail
        : detail && typeof detail === 'object'
          ? JSON.stringify(detail)
          : `Failed to execute node: ${response.statusText}`,
      response,
      errorData,
    )
  }

  return response.json()
}

/**
 * True when an executeNode failure proves no execution was admitted, so the
 * caller may clear its optimistic `running` status. A transport failure or a
 * generic 5xx may follow admission, and `execution_inputs_changed` means
 * another execution already owns the node; both are left to the backend.
 */
export function isDefinitiveExecuteNodeRefusal(error: Error): boolean {
  if (error instanceof InsufficientSparksError) return true
  if (!(error instanceof ApiRequestError)) return false
  if (error.status === 503) {
    return error.admission === 'draining' || error.admission === 'not-ready'
  }
  if (error.status < 400 || error.status >= 500) return false
  if (error.status !== 409) return true
  const detail = isApiRecord(error.errorData) ? error.errorData.detail : null
  return !(isApiRecord(detail) && detail.error === 'execution_inputs_changed')
}

export type LocationPanoramaExtractCount = 1 | 4 | 8

export interface CreateLocationPanoramaResponse {
  status: string
  source_node_id: string
  node_id: string
}

export interface ExtractLocationPanoramaViewsResponse {
  status: string
  source_node_id: string
  group_node_id?: string | null
  created_nodes: Array<{
    node_id: string
    asset_path: string
    yaw_degrees: number
    pitch_degrees: number
    fov_degrees: number
  }>
}

export async function createLocationPanorama(
  projectId: string,
  sourceNodeId: string,
  options: {
    canvasId: string
    view: Required<LocationPanoramaViewDict>
    name?: string
    run?: boolean
  },
): Promise<CreateLocationPanoramaResponse> {
  return apiRequest<CreateLocationPanoramaResponse>(
    `/projects/${projectId}/agentic/location-panorama/create`,
    {
      method: 'POST',
      body: JSON.stringify({
        source_node_id: sourceNodeId,
        canvas_id: options.canvasId,
        view: options.view,
        name: options.name,
        run: options.run ?? true,
      }),
    },
  )
}

export async function extractLocationPanoramaViews(
  projectId: string,
  sourceNodeId: string,
  options: {
    canvasId: string
    view: Required<LocationPanoramaViewDict>
    count: LocationPanoramaExtractCount
  },
): Promise<ExtractLocationPanoramaViewsResponse> {
  return apiRequest<ExtractLocationPanoramaViewsResponse>(
    `/projects/${projectId}/agentic/location-panorama/extract-views`,
    {
      method: 'POST',
      body: JSON.stringify({
        source_node_id: sourceNodeId,
        canvas_id: options.canvasId,
        view: options.view,
        count: options.count,
      }),
    },
  )
}

export async function writeVideoEditorEditSpec(
  projectId: string,
  nodeId: string,
  request: {
    editSpec: object
    expectedRevision: number
    expectedFingerprint: string | null
    canvasId: string
  },
): Promise<{ revision: number; fingerprint: string; compatibility_state: string }> {
  const response = await apiFetch(
    `/projects/${projectId}/agentic/video-editor/${encodeURIComponent(nodeId)}/edit-spec`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        edit_spec: request.editSpec,
        expected_revision: request.expectedRevision,
        expected_fingerprint: request.expectedFingerprint,
        compatibility_state: 'editspec_primary',
        canvas_id: request.canvasId,
      }),
    },
  )
  if (!response.ok) {
    const payload = await response.json().catch(() => null)
    throw new Error(payload?.detail ?? `EditSpec write failed (${response.status})`)
  }
  return response.json()
}

export interface DreamVibeCapabilities {
  edit_spec: boolean
  authoring_tools: boolean
  vibe_render: boolean
  live_preview: boolean
  speech_editing: boolean
  graphics: boolean
  intelligent_selection: boolean
  product_ads: boolean
  reference_remakes: boolean
  music_videos: boolean
  supported_edit_spec_versions: number[]
  supported_render_contract_versions: number[]
}

export async function getDreamVibeCapabilities(
  projectId: string,
  canvasId: string,
  signal?: AbortSignal,
): Promise<DreamVibeCapabilities> {
  const params = new URLSearchParams({ canvas_id: canvasId })
  const response = await apiFetch(
    `/projects/${projectId}/agentic/dreamvibe-capabilities?${params.toString()}`,
    { signal },
  )
  if (!response.ok) {
    throw new Error(`DreamVibe capabilities unavailable (${response.status})`)
  }
  return response.json()
}

/**
 * Cancel a running node execution and refund sparks.
 *
 * `canvasId` is REQUIRED — the backend scopes the cancel to this canvas's
 * orchestrator; omitting it falls back to the default "agent" canvas, so a
 * cancel on a secondary canvas targets the wrong (or no) running node.
 * Callers inside the workspace should pass `useActiveCanvasId()`.
 */
export async function cancelNodeExecution(
  projectId: string,
  nodeId: string,
  canvasId: string,
): Promise<{ ok: boolean; node_id: string }> {
  const query = `?canvas_id=${encodeURIComponent(canvasId)}`
  canvasExecuteLog({
    event: 'cancel-node-request',
    node_id: nodeId,
    canvas_id: canvasId,
  })
  const response = await apiFetch(
    `/projects/${projectId}/agentic/cancel-node/${nodeId}${query}`,
    { method: 'POST' },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(
      errorData?.detail || `Failed to cancel node: ${response.statusText}`,
    )
  }

  return response.json()
}

/**
 * Thrown by `executeNode` when the backend rejects with HTTP 402
 * `{ code: 'insufficient_sparks', message }`. Catch
 * it in the workspace layer and show <InsufficientSparksModal>.
 */
export class InsufficientSparksError extends Error {
  readonly code = 'insufficient_sparks'
  readonly balance: number | undefined = undefined
  readonly required: number | undefined = undefined
  constructor(messageOrLegacyBalance: string | number, _legacyRequired?: number, legacyMessage?: string) {
    super(typeof messageOrLegacyBalance === 'string' ? messageOrLegacyBalance : (legacyMessage ?? 'Insufficient credits'))
    this.name = 'InsufficientSparksError'
  }
}

function _isInsufficientSparksDetail(
  d: ApiValue | null,
): d is { code: 'insufficient_sparks'; message?: string } {
  if (!isApiRecord(d)) {
    return false
  }
  return d.code === 'insufficient_sparks'
}

// ---------------------------------------------------------------------------
// Publication API (RFC-0295 — Snapshot-based sharing)
// ---------------------------------------------------------------------------

export interface PublishResponse {
  share_token: string
  share_url: string
  version_number: number
  created_at: string
  /** Canvases that were NOT published. Empty for a single-canvas project. */
  excluded_canvas_titles?: string[]
}

export interface PublicationVersionInfo {
  version_number: number
  version_slug: string
  share_url: string
  created_at: string
}

export interface PublicationStatus {
  is_published: boolean
  share_token?: string
  share_url?: string
  version_number?: number
  updated_at?: string
  versions: PublicationVersionInfo[]
}

export interface PublishedQuestionOption {
  value?: string
  label?: string
  description?: string
}

export interface PublishedQuestion {
  answer?: string | null
  tool_use_id?: string
  question?: string
  context?: string
  options?: PublishedQuestionOption[]
}

export type PublishedNodeData = Record<string, ApiValue>

export interface PublishedChatMessage {
  role: 'user' | 'assistant'
  content: string
  agent?: string
  timestamp?: string
  referenced_node_ids?: string[]
  questions?: PublishedQuestion[]
}

export interface PublishedSnapshot {
  project_name: string
  project_id: string
  snapshot: {
    nodes: Array<{
      id: string
      type: string
      position: { x: number; y: number }
      data: PublishedNodeData
      parentNode?: string
      extent?: 'parent'
      width?: number
      height?: number
    }>
    edges: Array<{ id: string; source: string; target: string; type?: string; from_port: string; to_port: string; fromIndex?: number; toIndex?: number }>
    viewport?: { x: number; y: number; zoom: number }
  }
  messages: PublishedChatMessage[]
  published_at: string
}

export interface VersionListItem {
  version_number: number
  created_at: string
  project_name: string
}

export interface VersionDetail {
  project_name: string
  snapshot: PublishedSnapshot['snapshot']
  published_at: string
  version_number: number
}

export interface LiveSnapshot {
  project_name: string
  snapshot: PublishedSnapshot['snapshot']
  is_live: boolean
}

export interface VersionShareLink {
  share_token: string
  share_url: string
  version_number: number
}

export async function publishProject(
  projectId: string,
  acknowledgeExcludedCanvases = false,
): Promise<PublishResponse> {
  // Publishing captures only the main canvas and the published viewer cannot
  // show any other, so the API refuses a multi-canvas project until the caller
  // says it knows which canvases are being left out.
  return apiRequest(`/projects/${projectId}/publication`, {
    method: 'POST',
    body: JSON.stringify({ acknowledge_excluded_canvases: acknowledgeExcludedCanvases }),
  })
}

export async function unpublishProject(projectId: string): Promise<void> {
  await apiRequest<void>(`/projects/${projectId}/publication`, { method: 'DELETE' })
}

export async function getPublicationStatus(projectId: string): Promise<PublicationStatus> {
  return apiRequest(`/projects/${projectId}/publication`)
}

export async function getPublishedSnapshot(projectToken: string, versionSlug: string): Promise<PublishedSnapshot> {
  // Public endpoint — no auth needed (skipAuth still sends cookies if any).
  return apiRequest(`/publications/${projectToken}/${versionSlug}`, { skipAuth: true })
}

export async function getPublicationVersions(projectId: string): Promise<VersionListItem[]> {
  return apiRequest(`/projects/${projectId}/publication/versions`)
}

export async function getPublicationVersion(projectId: string, versionNumber: number): Promise<VersionDetail> {
  return apiRequest(`/projects/${projectId}/publication/versions/${versionNumber}`)
}

export async function getPublicationLive(projectId: string): Promise<LiveSnapshot> {
  return apiRequest(`/projects/${projectId}/publication/live`)
}

export async function createVersionShareLink(projectId: string, versionNumber: number): Promise<VersionShareLink> {
  return apiRequest(`/projects/${projectId}/publication/versions/${versionNumber}/share`, { method: 'POST' })
}

/**
 * Clone a published snapshot into the caller's workspace.
 *
 * RFC-1216: a source project can reference asset objects that are no longer in
 * the bucket. Those are skipped instead of failing the clone, and the result
 * reports the partial copy as typed fields — `message` is an untranslated
 * developer string and must never be parsed or rendered for that.
 */
export async function clonePublishedProject(
  projectToken: string,
  versionSlug: string,
): Promise<{
  id: string
  name: string
  message: string
  copied_asset_count: number
  expected_asset_count: number
  missing_asset_paths: string[]
}> {
  return apiRequest(`/publications/${projectToken}/${versionSlug}/clone`, { method: 'POST' })
}

export async function getTemplates(limit = 50, offset = 0): Promise<TemplateCatalogResponse> {
  // Public templates catalog (RFC-0934 P1) — no auth needed. `skipAuth` still
  // sends cookies, preserving DREAMWRIGHT_PROJECT LB affinity.
  return apiRequest(`/templates?limit=${limit}&offset=${offset}`, { skipAuth: true })
}
